/**
 * Раздел «Заказы» — главный экран, рабочий журнал лаборатории.
 *
 * Компоновка перенесена из демо: она вырабатывалась итерациями с заказчиком,
 * и каждый элемент здесь отвечает на вопрос, который техник задаёт списку
 * каждый день.
 *
 *   - сводка сверху: сколько всего, сколько в очереди и готово, на какую сумму
 *     и сколько из неё ещё не оплачено;
 *   - строка периода со своими итогами ПО ВЫБОРКЕ — они меняются вслед за
 *     фильтрами, в отличие от сводки за месяц;
 *   - таблица с колонками цвета и примечаний: и то и другое техник смотрит
 *     до того, как взять наряд в работу;
 *   - переключение статуса и оплаты прямо в строке, без открытия панели.
 *
 * Раздел под защитой несохранённых изменений (§6): правки видны сразу,
 * в базу уходят по кнопке «Сохранить изменения».
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Banknote,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Clock,
  CreditCard,
  FileText,
  Mail,
  MailCheck,
  Minus,
  Plus,
  Pin,
  RotateCcw,
  Search,
  Send,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import {
  composeLetter,
  constructionText,
  jawText,
  nameIndex,
  letterDue,
  formatDate,
  formatDateLong,
  formatKop,
  formatMonthKey,
  isFirstMonth,
  monthKeyForOffset,
  invoiceNumberText,
  needsInvoiceWarning,
  todayIso,
  urgencyOf,
  type Clinic,
  type PayMethod,
  pluralRu,
} from '@otc/shared';
import type { LetterKind } from '@otc/shared';
import { useSearchParams } from 'react-router-dom';
import { useBootstrap, useInvoices, useOrders, type ApiOrder } from '../api/queries.js';
import { api } from '../api/client.js';
import { useDrafts } from '../store/drafts.js';
import { useSync } from '../store/useSync.js';
import { useModule } from '../store/useModule.js';
import {
  Badge,
  Button,
  EmptyState,
  SectionHeader,
  SelectionActionButton,
  Spinner,
  Toast,
  useToast,
} from '../components/ui.js';
import { ConflictModal } from '../components/GuardModals.js';
import { StickyXScrollbar } from '../components/StickyXScrollbar.js';
import { useFillHeight } from '../components/useFillHeight.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { StatusQuickPicker } from '../components/StatusQuickPicker.js';
import { PaidQuickPicker } from '../components/PaidQuickPicker.js';
import { SendLetterDialog } from '../components/SendLetterDialog.js';
import { MultiSelectFilter, type FilterOption } from '../components/MultiSelectFilter.js';
import { DateField } from '../components/DateField.js';
import { Swatch } from '../components/Swatch.js';
import { useTableZoom, ZOOM_STEP } from '../components/useTableZoom.js';
import { useColumnWidths, type ColumnWidths } from '../components/useColumnWidths.js';
import { STATUS_COLORS } from '../theme.js';
import { blankOrder, OrderPanel, type OrderDraft } from './OrderPanel.js';
import { usePinnedOrderMonth, orderMonthOffset } from './usePinnedOrderMonth.js';
import { InvoiceCreateModal } from './InvoiceCreateModal.js';
import { InvoiceDetailModal } from './InvoiceDetailModal.js';
import { SummaryReportModal } from './SummaryReportModal.js';
import { подсказкаПоиска } from '../components/isNarrow.js';

type SortField =
  /** Порядок ЗАВЕДЕНИЯ — то, как заказы вносили, а не что в них написано. */
  | 'entered'
  | 'doctor'
  | 'clinic'
  | 'construction'
  | 'patient'
  | 'received'
  | 'due'
  | 'urgency'
  | 'cost'
  | 'expense'
  | 'paid';

/**
 * Ключи колонок журнала — в порядке разметки.
 *
 * По ним запоминаются заданные пользователем ширины, поэтому порядок и состав
 * обязаны совпадать с шапкой таблицы: сдвиг на одну колонку означал бы, что
 * сохранённая ширина «Стоимости» после обновления окажется у «Оплаты».
 */
const COLUMN_IDS = [
  'no',
  'select',
  'doctor',
  'clinic',
  'construction',
  'color',
  'notes',
  'patient',
  'received',
  'due',
  'status',
  'cost',
  'expense',
  'paid',
  'invoice',
] as const;

interface Filters {
  doctor: string[];
  clinic: string[];
  /** Основная конструкция заказа — без допов. */
  construction: string[];
  /** Доп. элементы заказа: печать модели, винт, крючки. */
  extra: string[];
  status: string[];
  pay: string[];
  paid: string[];
  /**
   * Дата сдачи — ОДИН день, а не список.
   *
   * Списком он был бы бесполезен: сроков в месяце три десятка, и выбирать
   * их галочками — то же листание, только в другом окне. Спрашивают же
   * обычно про один день: «что сдаём в пятницу».
   */
  due: string | null;
  /**
   * Только те, по которым ЗАБЫТ СЧЁТ — с треугольником в колонке «Счёт».
   *
   * Отдельным признаком, а не значением в списке: это не свойство заказа,
   * а вывод из статуса, способа оплаты, наличия счёта и настроек юрлица.
   * Держать его среди «Статуса оплаты» значило бы смешать «не оплачен» и
   * «не выставлен», а это разные беды: по первому ждут денег, по второму
   * даже не попросили.
   */
  needsInvoice: boolean;
  query: string;
}

const EMPTY_FILTERS: Filters = {
  doctor: [], clinic: [], construction: [], extra: [], status: [], pay: [], paid: [],
  due: null, needsInvoice: false, query: '',
};


const STATUS_OPTIONS: FilterOption[] = [
  { value: 'Очень срочно', label: 'Очень срочно', color: STATUS_COLORS.critical },
  { value: 'Срочно', label: 'Срочно', color: STATUS_COLORS.urgent },
  // «В работе» захватывает и срочное: это та же работа, просто её пора
  // делать раньше. Подробности — в отборе по статусу.
  { value: 'В работе', label: 'В работе', color: STATUS_COLORS.normal },
  { value: 'Ожидание', label: 'Ожидание', color: STATUS_COLORS.hold },
  { value: 'Готово', label: 'Готово', color: STATUS_COLORS.done },
];

const PAY_OPTIONS: FilterOption[] = [
  { value: 'По договору', label: 'По договору' },
  { value: 'Наличные', label: 'Наличные' },
];

const PAID_OPTIONS: FilterOption[] = [
  { value: 'yes', label: 'Оплачен', color: STATUS_COLORS.done },
  { value: 'no', label: 'Не оплачен', color: STATUS_COLORS.danger },
];

/** Уже заведённый заказ, похожий на тот, что заводят сейчас. */
interface DuplicateOrder {
  id: string;
  patient: string;
  doctorName: string;
  constructionName: string;
  costKop: number;
  received: string;
  status: string;
  createdAt: string;
}

export function Orders() {
  const today = todayIso();
  const { pinnedMonth, pinMonth, pinSaving } = usePinnedOrderMonth();
  const [monthOffset, setMonthOffset] = useState(() => orderMonthOffset(today, pinnedMonth));
  const [allMode, setAllMode] = useState(false);
  /**
   * Показывать только ОТМЕЧЕННЫЕ, независимо от месяца.
   *
   * Выделение и так сквозное: отметил пять заказов в июле, перешёл в август,
   * отметил ещё десять — действие применится ко всем пятнадцати. Но увидеть
   * их разом было нельзя, и приходилось прыгать по месяцам, проверяя, что
   * ничего не потерялось. Этот режим и показывает ровно то, что отмечено.
   */
  const [selectedOnly, setSelectedOnly] = useState(false);

  /**
   * Заказы одного счёта — режим, в который приходят из «Документов».
   *
   * Счёт часто собирается из заказов ДВУХ месяцев (§2.4 это разрешает), и
   * искать их помесячно бессмысленно: часть всегда останется за краем экрана.
   * Поэтому здесь месяц не участвует вовсе — отбор идёт по самому счёту.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const invoiceFocus = searchParams.get('invoice');

  const month = allMode || selectedOnly || invoiceFocus ? null : monthKeyForOffset(today, monthOffset);

  const bootstrap = useBootstrap(true);
  /**
   * Заказы грузятся ЦЕЛИКОМ, а месяц фильтруется на клиенте.
   *
   * Иначе выделение не переживает переключение месяца: выбранные в июле
   * заказы просто исчезали бы из памяти при переходе в август, и собрать
   * счёт из заказов двух месяцев (§2.4 это прямо разрешает) было бы нельзя.
   *
   * Объём подъёмный: лаборатория делает порядка сотни нарядов в месяц,
   * то есть несколько тысяч за годы работы — это сотни килобайт.
   */
  const ordersQuery = useOrders(null);
  const invoicesQuery = useInvoices(true);
  const module = useModule('orders');
  const { toast, show } = useToast();

  const [openOrder, setOpenOrder] = useState<{ draft: OrderDraft; isNew: boolean } | null>(null);
  /** Заводимый заказ, у которого нашёлся близнец, — до решения сотрудника. */
  const [duplicate, setDuplicate] = useState<{ draft: OrderDraft; found: DuplicateOrder[] } | null>(
    null,
  );
  /** Последнее состояние открытой карточки — на случай перехода к другому заказу. */
  const panelDraft = useRef<OrderDraft | null>(null);

  /*
   * Письмо врачу предлагается ПОСЛЕ сохранения, а не в момент правки.
   *
   * Оба повода — заведение заказа и перевод в «Готово» — сначала должны
   * доехать до сервера. Предложи письмо раньше, и оно ушло бы врачу о заказе,
   * которого ещё нет, или о готовности, которую потом не сохранили.
   *
   * Здесь копится, о чём спросить; окно показывается по одному.
   */
  const [letterQueue, setLetterQueue] = useState<{ orderId: string; kind: LetterKind }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Счёт, открытый по щелчку на его номере в строке заказа. */
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  /** Подтверждение отката несохранённых правок: вернуть отменённое нечем. */
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  /*
   * По умолчанию — ПОРЯДОК ЗАВЕДЕНИЯ, а не дата получения.
   *
   * Заказы вносят по одному, друг за другом, и эта последовательность и есть
   * рабочий журнал. Сортировка по дате получения ломала её при первой же
   * правке: стоило исправить дату приёма аппарата, и заказ уезжал вверх
   * к тем, что заводили позавчера.
   *
   * Сортировка по дате никуда не делась — она на своём столбце.
   */
  const [sort, setSort] = useState<{ field: SortField; dir: 'asc' | 'desc' }>({ field: 'entered', dir: 'asc' });
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [invoiceOrders, setInvoiceOrders] = useState<string[] | null>(null);
  /** `undefined` — окно закрыто; `null` — открыто без выделения. */
  const [reportOrders, setReportOrders] = useState<string[] | null | undefined>(undefined);

  useSync('orders', 'order', ordersQuery.data?.orders as never);

  const draftState = useDrafts((state) => state.modules.orders);
  /** ВСЕ заказы — по ним работает выделение, независимо от текущего месяца. */
  const orders = useMemo(() => {
    void draftState;
    return useDrafts.getState().entities<ApiOrder>('orders', 'order');
  }, [draftState]);

  /** Заказы текущего периода — по ним считается сводка и строится таблица. */
  const inPeriod = useMemo(() => {
    /*
      Порядок проверок задаёт старшинство режимов: счёт важнее отметок,
      отметки важнее месяца. Иначе, придя по ссылке из счёта с отмеченными
      заказами прошлого месяца, человек увидел бы не то, за чем шёл.
    */
    if (invoiceFocus) return orders.filter((order) => order.invoiceId === invoiceFocus);
    if (selectedOnly) return orders.filter((order) => selected.has(order.id));
    if (month === null) return orders;
    return orders.filter((order) => order.received.slice(0, 7) === month);
  }, [orders, month, selectedOnly, selected, invoiceFocus]);

  const data = bootstrap.data;
  const invoices = invoicesQuery.data?.invoices ?? [];

  /** Счёт, к заказам которого пришли по ссылке. */
  const focusedInvoice = invoiceFocus ? invoices.find((i) => i.id === invoiceFocus) ?? null : null;
  /** Помесячный просмотр — единственный режим, где стрелки месяцев осмысленны. */
  const byMonth = !allMode && !selectedOnly && !invoiceFocus;

  const сброситьСчёт = () => {
    if (!invoiceFocus) return;
    /*
      Убираем параметр адреса, а не просто прячем полосу: иначе обновление
      страницы возвращало бы человека в режим счёта, из которого он вышел.
      `replace` — чтобы «назад» вело туда, откуда пришли, а не по кругу.
    */
    const next = new URLSearchParams(searchParams);
    next.delete('invoice');
    setSearchParams(next, { replace: true });
  };

  /** Полный сброс вида журнала; личное закрепление остаётся в аккаунте. */
  const сброситьЖурнал = () => {
    setFilters(EMPTY_FILTERS);
    setSort({ field: 'entered', dir: 'asc' });
    setSelected(new Set());
    setSelectedOnly(false);
    setAllMode(false);
    сброситьСчёт();
    setMonthOffset(orderMonthOffset(today, pinnedMonth));
  };
  // --- Справочные подписи ----------------------------------------------------

  /*
   * Подпись врача. ПУСТОЙ id и ненайденный врач — разные вещи.
   *
   * Пусто — это «пока не известен»: работу привезли из клиники, а кто её ведёт,
   * выяснится позже. Такую строку показываем словами, а не прочерком, чтобы её
   * было видно и хотелось дописать. Прочерк остаётся для случая, когда врач в
   * заказе есть, но в справочнике его уже нет.
   */
  const doctorName = (id: string) => (id === '' ? '' : (data?.doctors.find((d) => d.id === id)?.name ?? '—'));
  const construction = (id: string) => data?.constructions.find((k) => k.id === id);

  /** Индекс справочника: нужен там, где название собирается вместе с допами. */
  const catalog = useMemo(() => nameIndex(data?.constructions ?? []), [data?.constructions]);

  /**
   * Какие письма по заказу уже ушли.
   *
   * Берётся ИЗ ОТВЕТА СЕРВЕРА, а не из черновика раздела, куда попадает всё
   * остальное. Черновик обновляется, только когда в разделе нет несохранённых
   * правок И у записи сменилась версия, — а отправка письма версию заказа не
   * меняет. Признак застревал дважды, и конверт менял цвет лишь после
   * «Сохранить изменения», хотя письмо давно ушло.
   *
   * Правило защиты правок при этом остаётся нетронутым: оно бережёт то, что
   * человек набрал, а список отправленных писем никто не набирает.
   */
  const sentLetters = useMemo(() => {
    const map = new Map<string, ('ACCEPTED' | 'READY')[]>();
    for (const order of ordersQuery.data?.orders ?? []) map.set(order.id, order.lettersSent ?? []);
    return map;
  }, [ordersQuery.data]);

  /**
   * Письма, отправленные в этом сеансе.
   *
   * Ответ сервера доедет через перезапрос, но отметка должна позеленеть в тот
   * же миг: между нажатием и обновлением списка человек успевает решить, что
   * не сработало, и отправить второй раз.
   */
  const [justSent, setJustSent] = useState<{ orderId: string; kind: LetterKind }[]>([]);

  /** Отправленные письма по заказу: с сервера плюс отправленные только что. */
  const lettersOf = (orderId: string): ('ACCEPTED' | 'READY')[] => [
    ...(sentLetters.get(orderId) ?? []),
    ...justSent.filter((x) => x.orderId === orderId).map((x) => x.kind),
  ];

  /**
   * Можно ли вообще писать этому врачу: есть почта и он не отказался.
   * Проверяется и здесь, и на сервере — отметка в списке не должна обещать
   * того, чего отправка не сделает.
   */
  const canNotify = (id: string) => {
    const doctor = data?.doctors.find((d) => d.id === id);
    return Boolean(doctor?.notify && doctor.email?.trim());
  };

  /**
   * Клиника и филиал. Филиал показывается ТОЛЬКО когда их у клиники больше
   * одного: у большинства клиник филиал единственный и назван так же, и строка
   * «Авандент · Авандент» была бы шумом в каждой строке.
   */
  const clinicLabel = (order: { clinicId: string; branchId: string }) => {
    const clinic = data?.clinics.find((c) => c.id === order.clinicId);
    /*
     * Заказ без клиники — частный пациент, и ячейка остаётся ПУСТОЙ.
     * Прочерк здесь читался бы как «данные потеряны», хотя их и не было.
     */
    if (!clinic) return { clinic: '', branch: null as string | null };
    if (clinic.branches.length <= 1) return { clinic: clinic.name, branch: null as string | null };
    return {
      clinic: clinic.name,
      branch: clinic.branches.find((b) => b.id === order.branchId)?.name ?? null,
    };
  };

  const clinicText = (order: { clinicId: string; branchId: string }) => {
    const label = clinicLabel(order);
    return label.branch ? `${label.clinic} ${label.branch}` : label.clinic;
  };

  /**
   * Названия цветов для строки таблицы.
   *
   * Служебный «Цвет не указан» здесь НЕ пишется словами: в колонке шириной
   * в шестьдесят пикселей он рассыпался на три строки и раздувал строку
   * заказа выше всех соседей — ради сообщения «ничего не выбрано». Штриховка
   * образца и подсказка при наведении говорят ровно то же и молча.
   * В карточке заказа название остаётся: там место есть.
   */
  const colorNames = (ids: string[]) =>
    ids
      .map((id) => data?.colors.find((c) => c.id === id))
      .filter((color) => color && !color.unspecified)
      .map((color) => color?.name ?? '')
      .join(', ');

  const extrasText = (order: ApiOrder) =>
    (order.extras ?? [])
      .map((extra) => {
        const item = construction(extra.id);
        return item ? `${item.name}${extra.qty > 1 ? ` ×${extra.qty}` : ''}` : null;
      })
      .filter(Boolean)
      .join(', ');

  const domainClinics = useMemo(() => (data?.clinics ?? []) as unknown as Clinic[], [data?.clinics]);

  /**
   * Ждёт ли заказ счёта — ТО ЖЕ ПРАВИЛО, что у треугольника в колонке «Счёт».
   *
   * Правило живёт в `needsInvoiceWarning` и сложнее, чем кажется: заказ должен
   * быть ГОТОВ, по договору, без счёта и без перенесённого бумажного номера,
   * а у юрлица должен стоять флаг «напоминать о счёте» и не стоять «сводить
   * в одну строку» — такие клиники платят за месяц разом, и напоминание по
   * каждому заказу звало бы делать ненужное.
   *
   * Своё, упрощённое правило здесь было ошибкой: значок в сводке загорался
   * на заказах, у которых треугольника в строке нет, — два ответа на один
   * вопрос в одном окне.
   */
  const ждётСчёта = useCallback(
    (order: ApiOrder) =>
      needsInvoiceWarning(
        {
          status: order.status,
          pay: order.pay as PayMethod,
          invoiceId: order.invoiceId,
          legacyInvoiceNo: order.legacyInvoiceNo,
          clinicId: order.clinicId,
          branchId: order.branchId,
        },
        domainClinics,
      ),
    [domainClinics],
  );

  /**
   * Порядковый номер заказа.
   *
   * Считается по ПОРЯДКУ ЗАВЕДЕНИЯ среди всех заказов месяца.
   *
   * Не по позиции в отфильтрованном списке: иначе номер прыгал бы при каждой
   * смене фильтра и перестал бы что-либо значить. И не по дате получения:
   * дату правят задним числом, и вся нумерация месяца сдвигалась от одной
   * такой правки.
   *
   * Порядок заведения не меняется никогда — на него можно ссылаться в
   * разговоре с врачом.
   */
  const numbering = useMemo(() => {
    /*
      Номер считается в пределах МЕСЯЦА ПОЛУЧЕНИЯ заказа — всегда, в каком бы
      режиме ни был открыт журнал.

      Раньше он считался по тому, что показано на экране, и один и тот же
      заказ был «12-м» в помесячном просмотре, «213-м» в режиме «все заказы»
      и «3-м» среди заказов счёта. Номер, меняющийся от нажатия кнопки, ни на
      что не годится: на него ссылаются в разговоре с врачом.
    */
    const поМесяцам = new Map<string, ApiOrder[]>();
    for (const order of orders) {
      const месяц = order.received.slice(0, 7);
      const список = поМесяцам.get(месяц) ?? [];
      список.push(order);
      поМесяцам.set(месяц, список);
    }

    const карта = new Map<string, number>();
    for (const список of поМесяцам.values()) {
      список
        .sort((a, b) => creationOrder(a).localeCompare(creationOrder(b)))
        .forEach((order, index) => карта.set(order.id, index + 1));
    }
    return карта;
  }, [orders]);

  // --- Фильтрация и сортировка -----------------------------------------------

  /** Какой из фильтров не применять — чтобы узнать, что в нём ещё можно выбрать. */
  type КлючОтбора =
    | 'doctor' | 'clinic' | 'construction' | 'extra' | 'status' | 'pay' | 'paid' | 'due' | 'needsInvoice';

  /** Метка статуса так, как она показана в колонке. Подробности — в `проходит`. */
  const меткаСтатуса = (order: ApiOrder) =>
    order.status === 'Ожидание' ? 'Ожидание' : urgencyOf(order, today).label;

  /*
   * Проходит ли заказ фильтры — ОДНА функция и для списка, и для самих фильтров.
   *
   * `кроме` пропускает один фильтр. Так строятся варианты в каждом списке:
   * «какие клиники есть среди заказов, подходящих под ВСЕ ОСТАЛЬНЫЕ условия».
   * Прежде списки врачей и клиник строились из всех заказов месяца, а статус и
   * оплата были постоянными: отметил «Не оплачен» — а в клиниках по-прежнему
   * все подряд, половина из которых ничего не покажет.
   */
  const проходит = (order: ApiOrder, кроме?: КлючОтбора): boolean => {
    const query = filters.query.trim().toLowerCase();

    {
      if (кроме !== 'doctor' && filters.doctor.length && !filters.doctor.includes(order.doctorId)) return false;
      if (кроме !== 'clinic' && filters.clinic.length && !filters.clinic.includes(order.clinicId)) return false;
      // Отбор по ОСНОВНОЙ конструкции: допы в заказе на него не влияют.
      if (
        кроме !== 'construction' &&
        filters.construction.length &&
        !filters.construction.includes(order.constructionId)
      )
        return false;
      /*
       * Доп. элементы: заказ подходит, если в нём есть ХОТЬ ОДИН отмеченный.
       * Требовать все разом незачем — так ищут «где мы ставили винт», а не
       * «где стояли ровно эти три вещи сразу».
       */
      if (кроме !== 'extra' && filters.extra.length) {
        const свои = new Set((order.extras ?? []).map((доп) => доп.id));
        if (!filters.extra.some((id) => свои.has(id))) return false;
      }

      if (кроме !== 'pay' && filters.pay.length && !filters.pay.includes(order.pay)) return false;

      if (кроме !== 'paid' && filters.paid.length) {
        const wanted = order.paid ? 'yes' : 'no';
        if (!filters.paid.includes(wanted)) return false;
      }

      /*
        Дата сдачи сверяется БУКВАЛЬНО, по календарному дню.

        Заказ без срока не проходит никогда: «что сдаём в пятницу» — вопрос
        про назначенные сроки, а у такого заказа срока попросту нет.
      */
      if (кроме !== 'due' && filters.due && order.due !== filters.due) return false;

      if (кроме !== 'needsInvoice' && filters.needsInvoice && !ждётСчёта(order)) return false;

      /**
       * Статус сверяется с тем, что ПОКАЗАНО в колонке, а не с хранимым полем:
       * при значении «Авто» срочность вычисляется по дате сдачи и в базе
       * не лежит, поэтому отбор «Срочно» иначе не нашёл бы ничего.
       */
      if (кроме !== 'status' && filters.status.length) {
        /*
          «Ожидание» отбирается по САМОМУ статусу, а не по метке срочности.
          Замороженный заказ показывает метку «В работе» — она отражает
          состояние работы, — и без этой ветки отбор «В работе» захватывал бы
          заодно все замороженные, а отбор «Ожидание» не находил бы ничего.
        */
        const метка = меткаСтатуса(order);

        /*
         * «В РАБОТЕ» ВКЛЮЧАЕТ В СЕБЯ СРОЧНОЕ.
         *
         * Метка срочности вычисляется по сроку сдачи: у заказа в работе она
         * становится «Срочно» за четыре дня до срока и «Очень срочно» за три.
         * Это не другое состояние работы, а та же работа, которую пора делать
         * раньше прочей.
         *
         * Отбор сравнивал метки буквально, и «В работе» показывал ровно то,
         * до чего ещё далеко, — а самое горящее из выборки выпадало. Спросить
         * «что у меня в работе» и не увидеть срочного — худший из возможных
         * ответов.
         *
         * Обратное не делается: «Очень срочно» и «Срочно» по-прежнему
         * отбирают ровно себя — ими как раз сужают список до горящего.
         */
        const подходит =
          filters.status.includes(метка) ||
          (filters.status.includes('В работе') &&
            (метка === 'Очень срочно' || метка === 'Срочно'));
        if (!подходит) return false;
      }

      if (query) {
        /*
          В поиск входит НОМЕР СЧЁТА.

          Так поднимаются все заказы одного счёта сразу: ввёл «374» —
          получил ровно те строки, выделил все и отметил оплаченными. Раньше
          для этого приходилось открывать счёт, выписывать пациентов и
          искать их по одному.

          «№» перед числом отбрасывается: его пишут по привычке, и без этого
          поиск «№374» не находил бы ничего.
        */
        const число = query.replace(/^№\s*/, '').trim();
        /*
          В поиск входит и САМА РАБОТА: конструкция с допами и челюстью, цвета
          и примечания. Прежде искались только пациент, врач и клиника, и найти
          все «Haas» месяца или заказ с пометкой «переделка» было нельзя — только
          листать. Отбор «Все конструкции» ищет по основе, а поиск — по всему
          тексту работы, включая допы вроде «крючки».
        */
        const haystack = [
          order.patient,
          doctorName(order.doctorId),
          clinicText(order),
          constructionText(order, catalog),
          colorNames(order.colors),
          order.notes,
        ]
          .join(' ')
          .toLowerCase();
        /*
          Перенесённый номер ищется наравне с настоящим: для того, кто ищет,
          «счёт 344» — это счёт 344, и неважно, выписан он в программе или
          перенесён с бумаги вместе со старыми заказами.
        */
        const счета = [order.invoiceNumber, order.legacyInvoiceNo]
          .filter((n): n is number => n !== null)
          .map(String);
        if (!haystack.includes(query) && !(число && счета.includes(число))) return false;
      }
      return true;
    }
  };

  /**
   * Варианты в фильтрах — ТОЛЬКО ТЕ, ПРИ КОТОРЫХ ЧТО-ТО НАЙДЁТСЯ.
   *
   * Каждый список строится по заказам периода, подходящим под все ОСТАЛЬНЫЕ
   * выбранные условия. Отметил «Не оплачен» — в клиниках остались те, у кого
   * есть неоплаченное; выбрал клинику — во врачах её врачи, в статусах те, что
   * у её заказов бывают. Свой собственный выбор список не сужает: иначе после
   * первой же галочки в нём не осталось бы, что добавить.
   *
   * Уже ОТМЕЧЕННОЕ из списка не пропадает, даже если под остальные условия у
   * него ничего нет: оно помечается «нет в выборке». Иначе отбор действовал бы,
   * а снять его было бы нечем — строка исчезла из меню, а экран пуст.
   */
  const filterOptions = useMemo(() => {
    const заказыБез = (ключ: КлючОтбора) => inPeriod.filter((order) => проходит(order, ключ));

    const build = (
      ключ: 'doctor' | 'clinic' | 'construction',
      поле: 'doctorId' | 'clinicId' | 'constructionId',
      chosen: string[],
      all: readonly { id: string; name: string }[],
    ) => {
      const present = new Set(заказыБез(ключ).map((order) => order[поле]).filter(Boolean));
      const names = new Map(all.map((item) => [item.id, item.name]));

      const options = [...present].map((id) => ({
        value: id,
        label: names.get(id) ?? 'удалён из справочника',
      }));

      for (const id of chosen) {
        if (present.has(id)) continue;
        options.push({ value: id, label: `${names.get(id) ?? 'удалён'} — нет в выборке` });
      }

      return options.sort((a, b) => a.label.localeCompare(b.label, 'ru'));
    };

    /*
     * Постоянные списки (статус, способ оплаты, оплата) сужаются так же, но
     * порядок в них прежний, заданный, — по алфавиту «Очень срочно» уехало бы
     * в середину.
     */
    const оставить = (
      options: FilterOption[],
      chosen: string[],
      есть: (value: string) => boolean,
    ) => options.filter((option) => chosen.includes(option.value) || есть(option.value));

    const метки = new Set(заказыБез('status').map(меткаСтатуса));
    const способы = new Set(заказыБез('pay').map((order) => order.pay));
    const оплаты = new Set<string>(заказыБез('paid').map((order) => (order.paid ? 'yes' : 'no')));

    /*
     * Допы лежат внутри заказа списком, поэтому обычный `build` по полю не
     * годится: собираем их вручную из всех заказов выборки.
     */
    const допыВыборки = new Set<string>();
    for (const order of заказыБез('extra')) {
      for (const доп of order.extras ?? []) допыВыборки.add(доп.id);
    }
    const именаКонструкций = new Map((data?.constructions ?? []).map((к) => [к.id, к.name]));
    const extras = [...допыВыборки]
      .map((id) => ({ value: id, label: именаКонструкций.get(id) ?? 'удалён из справочника' }))
      .concat(
        filters.extra
          .filter((id) => !допыВыборки.has(id))
          .map((id) => ({
            value: id,
            label: `${именаКонструкций.get(id) ?? 'удалён'} — нет в выборке`,
          })),
      )
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'));

    return {
      doctors: build('doctor', 'doctorId', filters.doctor, data?.doctors ?? []),
      extras,
      clinics: build('clinic', 'clinicId', filters.clinic, data?.clinics ?? []),
      constructions: build('construction', 'constructionId', filters.construction, data?.constructions ?? []),
      // «В работе» захватывает и срочное — значит, есть, если есть хоть что-то из трёх.
      statuses: оставить(STATUS_OPTIONS, filters.status, (value) =>
        value === 'В работе'
          ? метки.has('В работе') || метки.has('Срочно') || метки.has('Очень срочно')
          : метки.has(value as ReturnType<typeof меткаСтатуса>),
      ),
      pays: оставить(PAY_OPTIONS, filters.pay, (value) => способы.has(value as ApiOrder['pay'])),
      paid: оставить(PAID_OPTIONS, filters.paid, (value) => оплаты.has(value)),
      /*
        Дни, на которые что-то сдаётся, — по тому же правилу, что остальные
        списки: считаются по заказам, прошедшим ВСЕ ОСТАЛЬНЫЕ условия. Выбрал
        врача — в календаре пожирнели только его сроки, и видно, в какие дни
        к нему вообще что-то едет.
      */
      дниСдачи: new Set(
        заказыБез('due')
          .map((order) => order.due)
          .filter((дата): дата is string => Boolean(дата)),
      ),
    };
    // `проходит` зависит ровно от этих значений.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inPeriod, filters, today, data?.doctors, data?.clinics, data?.constructions]);

  const visible = useMemo(() => {
    const filtered = inPeriod.filter((order) => проходит(order));

    const value = (order: ApiOrder): string | number => {
      switch (sort.field) {
        case 'entered': return creationOrder(order);
        case 'doctor': return doctorName(order.doctorId);
        case 'clinic': return clinicText(order);
        case 'construction': return construction(order.constructionId)?.name ?? '';
        case 'patient': return order.patient;
        case 'received': return order.received;
        // Заказы без срока уходят в конец: сортируют, чтобы увидеть ближайшие.
        case 'due': return order.due ?? '9999-99-99';
        case 'urgency': return urgencyOf(order, today).label;
        case 'cost': return order.costKop;
        case 'expense': return order.expenseKop;
        case 'paid': return order.paid ? 1 : 0;
      }
    };

    return [...filtered].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      const cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'ru');
      if (cmp !== 0) return sort.dir === 'asc' ? cmp : -cmp;

      /*
       * Равные значения разрешаются ПОРЯДКОМ ЗАВЕДЕНИЯ, и всегда по
       * возрастанию — независимо от направления основной сортировки.
       *
       * За день приходит несколько заказов с одной датой получения, и без
       * запасного ключа они выстраивались как придётся: заведённый вторым
       * оказывался выше первого. Направление тут не переворачивается
       * намеренно — «в каком порядке я их заводил» не должно зависеть от
       * того, каким концом отсортирован соседний столбец.
       */
      return creationOrder(a).localeCompare(creationOrder(b));
    });
  }, [inPeriod, filters, sort, data, today]);

  // --- Сводки ----------------------------------------------------------------

  const zoom = useTableZoom();

  /*
    Ширина колонок задаётся пользователем и запоминается на этом рабочем месте.

    Ни один набор ширин не подходит всем сразу: на широком мониторе владельца
    хочется видеть полное название конструкции, на ноутбуке техника — деньги
    и сроки. Спорить об умолчании бессмысленно, поэтому границы просто
    тянутся мышью, а результат остаётся до следующего раза.
  */
  const columns = useColumnWidths('otc.orders.columns');
  const tableRef = useRef<HTMLTableElement>(null);
  /** Блок с горизонтальной прокруткой — за ним следит закреплённая полоса. */
  const tableScrollRef = useRef<HTMLDivElement>(null);

  /*
   * ПРОКРУЧИВАЮТСЯ СТРОКИ, а не страница: шапка раздела и шапка таблицы
   * (она `sticky`) остаются на виду, сколько бы заказов ни было. Подробности
   * и причина — в `useFillHeight`.
   */
  const { ref: журналRef, высота: высотаЖурнала } = useFillHeight();


  /**
   * Захват границы колонки.
   *
   * При первом перетаскивании снимаются ширины ВСЕХ колонок — такими, какими
   * они сейчас на экране. Без этого снимка переход на жёсткую раскладку
   * схлопнул бы соседние колонки до нуля: у них не было бы ни одной заданной
   * ширины, и таблица прыгнула бы целиком.
   */
  const startResize = useCallback(
    (id: string, event: React.PointerEvent<HTMLElement>) => {
      const head = event.currentTarget.parentElement as HTMLElement | null;
      if (!head) return;

      const snapshot: ColumnWidths = {};
      const heads = tableRef.current?.querySelectorAll('thead th');
      heads?.forEach((cell, index) => {
        const key = COLUMN_IDS[index];
        if (key) snapshot[key] = Math.round(cell.getBoundingClientRect().width);
      });

      const startX = event.clientX;
      const startWidth = head.getBoundingClientRect().width;

      const move = (moveEvent: PointerEvent) => {
        columns.setWidth(id, startWidth + (moveEvent.clientX - startX), snapshot);
      };
      const finish = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', finish);
        document.body.style.cursor = '';
      };

      // Курсор держим на всём окне: увести его с узкой полоски легко,
      // и без этого он мигал бы между стрелкой и текстовым.
      document.body.style.cursor = 'col-resize';
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', finish);
    },
    [columns],
  );

  /*
   * Фильтры на телефоне СВЁРНУТЫ.
   *
   * Развёрнутыми они занимают шесть строк — почти треть экрана, — и до самих
   * заказов приходится прокручивать полстраницы. Пользуются ими редко, а
   * журнал смотрят каждый раз.
   *
   * На широком экране разворачивание не нужно: место есть.
   */
  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Включён ли отбор «только срочное» — им управляет кнопка в сводке.
   *
   * Состояние берётся из самого фильтра, а не из отдельного флажка: тогда
   * снятие отбора в списке «Статус изготовления» гасит и кнопку, и они не
   * могут разойтись.
   */
  const срочныеВключены =
    filters.status.length === 2 &&
    filters.status.includes('Срочно') &&
    filters.status.includes('Очень срочно');

  /**
   * ТРЕУГОЛЬНИК «ЗАБЫТ СЧЁТ» — сводный счётчик строк с таким же значком.
   *
   * Тот же значок и то же правило, что в колонке «Счёт»: аппарат готов,
   * оплата по договору, счёта нет, а юрлицо просит напоминать. Нажатие
   * оставляет в журнале ровно эти заказы.
   *
   * Отдельной заготовкой, потому что ставится в ДВУХ местах: на широком
   * экране в пустоте между деньгами и правым краем сводки, на телефоне —
   * рядом со «Срочными работами» (место выбрано владельцем 27.09.2026).
   * Две копии разметки однажды разошлись бы видом.
   *
   * Когда счетов не ждёт никто, значка НЕТ вовсе: серый треугольник
   * «всё хорошо» — противоречие, предупреждение либо есть, либо нет.
   */
  const треугольникСчетов = (className: string) =>
    stats.безСчёта > 0 ? (
      <button
        /*
          Быстрые отборы ВЗАИМНО ИСКЛЮЧАЮТ друг друга.

          «Забыт счёт» требует статуса «Готово», а «Срочные работы» —
          «Срочно» или «Очень срочно». Вместе это пустой журнал: заказ не
          бывает готовым и срочным одновременно. Раньше они складывались,
          и, нажав одну кнопку поверх другой, человек получал ноль строк
          при двух горящих кнопках — выглядело как поломка.

          Поэтому включение одного снимает другой: это не два условия,
          а два разных вопроса к журналу.
        */
        onClick={() => {
          setAllMode(!filters.needsInvoice);
          setSelectedOnly(false);
          сброситьСчёт();
          setFilters({ ...EMPTY_FILTERS, needsInvoice: !filters.needsInvoice });
        }}
        aria-pressed={filters.needsInvoice}
        title={
          filters.needsInvoice
            ? 'Вернуться к выбранному месяцу'
            : `Готово, но счёт не выставлен: ${stats.безСчёта}. Показать за все месяцы`
        }
        className={`flex-shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 transition-colors hover:bg-black/5 ${className}`}
        style={{
          // Цвет — ТОТ ЖЕ, что у значка в строке заказа: один смысл, один цвет.
          color: STATUS_COLORS.warning,
          background: filters.needsInvoice
            ? `color-mix(in srgb, ${STATUS_COLORS.warning} 16%, transparent)`
            : undefined,
        }}
      >
        {/*
          Значок КРУПНЕЕ прочих (28 против обычных 14–20).

          Это единственное в сводке, что зовёт сделать работу, а не просто
          сообщает число, — и рядом с плашкой «Срочные работы» он должен
          перебивать её взглядом, а не теряться. Линия тоньше обычной: при
          таком размере стандартные две точки выглядят грубо.
        */}
        <AlertTriangle size={28} strokeWidth={1.75} className="flex-shrink-0" />
        {/* Число рядом со значком: сам треугольник говорит «что-то забыто»,
            а сколько именно — иначе пришлось бы наводить мышь. */}
        <span className="tabular text-lg font-semibold">{stats.безСчёта}</span>
      </button>
    ) : null;

  /** На сколько месяцев вперёд или назад отстоит дата от сегодняшней. */
  const смещениеМесяца = (от: string, дата: string) =>
    (Number(дата.slice(0, 4)) - Number(от.slice(0, 4))) * 12 +
    (Number(дата.slice(5, 7)) - Number(от.slice(5, 7)));

  const stats = useMemo(() => {
    const done = inPeriod.filter((o) => o.status === 'Готово').length;
    // Ожидание считается отдельно: замороженный заказ не стоит в очереди —
    // его никто не ждёт, и в общем счёте работы он создаёт ложную нагрузку.
    const hold = inPeriod.filter((o) => o.status === 'Ожидание').length;

    /*
      В ДЕНЬГАХ замороженный заказ считается НУЛЁМ.

      Он висит в списке и виден — работа никуда не делась, — но пока врач его
      не разморозил, денег за него нет: ни выручки, ни долга, ни расхода на
      материалы. Иначе долг по клинике включал бы работу, которую никто не
      делал и за которую никто не выставит счёт.

      В счётчиках заказов он при этом участвует: их считают по строкам списка,
      и «Всего заказов» обязано совпадать с тем, что видно на экране.
    */
    const вДеньгах = inPeriod.filter((o) => o.status !== 'Ожидание');

    /*
      ТРЕБУЮТ ВНИМАНИЯ — срочное и очень срочное вместе.

      Метка срочности вычисляется по сроку сдачи, в базе её нет, поэтому
      считается тем же способом, что и в колонке «Статус»: иначе число
      в сводке и строки в журнале разошлись бы.
    */
    // Предупреждения охватывают все месяцы: смена месяца не закрывает работу.
    const срочных = orders.filter((o) => {
      const метка = меткаСтатуса(o);
      return метка === 'Срочно' || метка === 'Очень срочно';
    }).length;

    /*
      ЗАБЫТ СЧЁТ — аппарат готов, а денег за него ещё не попросили.

      Считается ровно по тем строкам, где в колонке «Счёт» стоит треугольник:
      значок в сводке — это их счётчик, а не второе мнение.
    */
    const безСчёта = orders.filter(ждётСчёта).length;

    return {
      total: inPeriod.length,
      queued: inPeriod.length - done - hold,
      done,
      hold,
      срочных,
      безСчёта,
      sumKop: вДеньгах.reduce((sum, o) => sum + o.costKop, 0),
      debtKop: вДеньгах.filter((o) => !o.paid).reduce((sum, o) => sum + o.costKop, 0),
      // Во что заказы периода обошлись лаборатории — рядом с выручкой.
      expenseKop: вДеньгах.reduce((sum, o) => sum + o.expenseKop, 0),
    };
    // `today` — потому что от него считается срочность, `ждётСчёта` — потому
    // что правило зависит от настроек юрлиц.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inPeriod, orders, today, ждётСчёта]);

  /**
   * ОСТАТОК месяца — выручка за вычетом материалов и аренды.
   *
   * Ровно то число, от которого считается доля сотрудников: в «Сотрудниках»
   * оно стоит плиткой «Зарплатная база», в «Финансах» — строкой расчёта.
   * Налог и прочие расходы из него НЕ вычитаются (правило владельца от
   * 16.09.2026): они обязательства лаборатории, а не условие оплаты труда.
   *
   * Считается по заказам месяца ДО фильтров и без замороженных — как и
   * остальные числа сводки, иначе отбор по врачу менял бы «остаток».
   */
  /*
   * Аренда приходит ВСЕМ, а не только владельцу.
   *
   * 19.09.2026 нашлось расхождение: у владельца «Остаток» был 780 630, у
   * сотрудника — 865 630, ровно на месячную аренду больше. Сервер скрывал
   * аренду от сотрудников, и у них из суммы она не вычиталась: подпись одна,
   * число разное. Владелец решил, что остаток — величина общая (выручка минус
   * материалы и аренда), и видеть её должны все одинаково.
   */
  const rentKop = data?.settings?.rentKop ?? 0;
  const остатокKop = stats.sumKop - stats.expenseKop - rentKop;
  /* Замороженные и здесь считаются нулём — правило одно на весь журнал. */
  const visibleВДеньгах = visible.filter((o) => o.status !== 'Ожидание');
  const selectionSumKop = visibleВДеньгах.reduce((sum, o) => sum + o.costKop, 0);
  const selectionDebtKop = visibleВДеньгах.filter((o) => !o.paid).reduce((sum, o) => sum + o.costKop, 0);
  /*
    Расход по выборке — во что эти заказы обошлись лаборатории.

    Рядом с выручкой он отвечает на вопрос, ради которого в журнал и смотрят
    с фильтром: сколько на этом заработано. Сумма без расхода показывает
    оборот, а не заработок, и по ней легко принять убыточный месяц за удачный.
  */
  const selectionExpenseKop = visibleВДеньгах.reduce((sum, o) => sum + o.expenseKop, 0);

  /**
   * Первый повод из очереди, пригодный к отправке.
   *
   * Неприменимые отбрасываются молча: у врача нет почты или он отказался от
   * уведомлений, письмо такого вида уже уходило, заказ успели удалить. Это не
   * ошибки — уведомление остаётся ВОЗМОЖНОСТЬЮ, а не условием работы, и
   * заводить заказ врачу без почты ничто не мешает.
   */
  const letterAsk = useMemo(() => {
    const templates = data?.settings?.templates;
    if (!templates) return null;

    for (const item of letterQueue) {
      const order = orders.find((o) => o.id === item.orderId);
      if (!order) continue;

      const doctor = data.doctors.find((d) => d.id === order.doctorId);
      const toEmail = doctor?.email?.trim() ?? '';
      if (!doctor || !doctor.notify || !toEmail) continue;
      if (order.lettersSent?.includes(item.kind)) continue;

      return {
        orderId: order.id,
        kind: item.kind,
        doctorName: doctor.name,
        toEmail,
        letter: composeLetter(item.kind, templates, {
          doctor: doctor.name,
          patient: order.patient,
          /*
            В письмо идёт конструкция ВМЕСТЕ С ДОП. ЭЛЕМЕНТАМИ. Печать модели,
            крючки, искусственные зубы врач заказывает сам и по ним же сверяет
            полученное — а видел в письме только название базовой конструкции.
          */
          construction: constructionText(order, catalog),
          due: order.due ? formatDate(order.due) : 'срок не назначен',
        }),
      };
    }
    return null;
  }, [letterQueue, orders, data, construction]);

  /*
   * Поводы, которые показывать не о чем, из очереди убираются: иначе она
   * росла бы весь сеанс и первый неприменимый повод загораживал бы следующие.
   */
  useEffect(() => {
    if (letterQueue.length === 0 || letterAsk) return;
    setLetterQueue([]);
  }, [letterQueue, letterAsk]);

  /**
   * Что показать в карточке заказа про письмо врачу.
   *
   * `null` — писать не о чем: заказ ещё не создан (письмо ссылалось бы на
   * несуществующий заказ) или врача с такой почтой нет. Причина отказа
   * возвращается словами: молчащая кнопка хуже отсутствующей.
   */
  const letterStateFor = (open: { draft: OrderDraft; isNew: boolean }) => {
    if (open.isNew) return null;

    const doctor = data?.doctors.find((d) => d.id === open.draft.doctorId);
    if (!doctor) return null;

    // Сохранённый статус берётся с СЕРВЕРА: в черновике он уже мог смениться,
    // а письмо должно опираться на то, что записано.
    const saved = ordersQuery.data?.orders.find((o) => o.id === open.draft.id);
    if (!saved) return null;

    const blocked = !doctor.email?.trim()
      ? 'У врача не указана почта — письмо отправить некуда'
      : !doctor.notify
        ? 'Врач отказался от уведомлений'
        : null;

    return { savedStatus: saved.status, sentKinds: lettersOf(saved.id), blocked };
  };

  // --- Действия ---------------------------------------------------------------

  const update = (id: string, patch: Record<string, unknown>) => {
    // Стал «Готово» — после сохранения предложим письмо о готовности.
    const was = orders.find((o) => o.id === id)?.status;
    if (patch.status === 'Готово' && was !== 'Готово') {
      setLetterQueue((queue) =>
        queue.some((item) => item.orderId === id && item.kind === 'READY')
          ? queue
          : [...queue, { orderId: id, kind: 'READY' }],
      );
    }
    useDrafts.getState().update('orders', 'order', id, patch);
  };

  /**
   * Заведение заказа с проверкой на повтор.
   *
   * Проверяются только НОВЫЕ заказы: правка существующего повтором быть не
   * может. Отказ проверки работе не мешает — она подсказка, и молчание
   * сервера не повод не дать завести заказ.
   */
  const submitOrder = async (draft: OrderDraft, confirmed: boolean, держатьОткрытой = false) => {
    if (!draft.id && !confirmed) {
      const found = await findDuplicates(draft);
      if (found.length > 0) {
        setDuplicate({ draft, found });
        return;
      }
    }
    applyOrder(draft, держатьОткрытой);
  };

  const findDuplicates = async (draft: OrderDraft): Promise<DuplicateOrder[]> => {
    if (!draft.patient.trim()) return [];
    try {
      const params = new URLSearchParams({
        patient: draft.patient.trim(),
        received: draft.received,
        costKop: String(draft.costKop),
      });
      if (draft.doctorId) params.set('doctorId', draft.doctorId);
      const result = await api.get<{ orders: DuplicateOrder[] }>(
        `/api/orders/duplicates?${params.toString()}`,
      );
      return result.orders;
    } catch {
      // Проверка — подсказка, а не преграда: не ответила, значит заводим.
      return [];
    }
  };

  const saveOrder = (draft: OrderDraft, держатьОткрытой = false) =>
    void submitOrder(draft, false, держатьОткрытой);

  const applyOrder = (draft: OrderDraft, держатьОткрытой = false) => {
    const store = useDrafts.getState();
    const payload = {
      doctorId: draft.doctorId, clinicId: draft.clinicId, branchId: draft.branchId,
      constructionId: draft.constructionId, constructionQty: draft.constructionQty,
      jaw: draft.jaw,
      colors: draft.colors, extras: draft.extras,
      patient: draft.patient, received: draft.received, due: draft.due,
      status: draft.status, urgency: draft.urgency, pay: draft.pay, paid: draft.paid,
      costKop: draft.costKop, expenseKop: draft.expenseKop, notes: draft.notes,
    };
    const tempId = draft.id ? null : store.create('orders', 'order', payload);
    if (draft.id) {
      store.update('orders', 'order', draft.id, payload);
      // Правка через карточку тоже может перевести заказ в «Готово».
      if (payload.status === 'Готово' && orders.find((o) => o.id === draft.id)?.status !== 'Готово') {
        setLetterQueue((queue) =>
          queue.some((item) => item.orderId === draft.id && item.kind === 'READY')
            ? queue
            : [...queue, { orderId: draft.id!, kind: 'READY' }],
        );
      }
    }
    /*
     * Карточка закрывается — КРОМЕ случая, когда из неё открывают документ.
     *
     * Кнопки «Создать счёт/акт» и «Создать отчёт» сперва применяют правки:
     * счёт должен уйти с той ценой, которую человек видит. Но применение
     * закрывало карточку, окно документа открывалось поверх пустого журнала,
     * и, закрыв его, человек терял заказ, с которым работал, — приходилось
     * искать ту же строку заново.
     */
    if (!держатьОткрытой) setOpenOrder(null);

    /*
     * Заказ уходит на сервер СРАЗУ, а не ждёт общей кнопки «Сохранить
     * изменения».
     *
     * Пакетное сохранение остаётся для справочников, где правят помногу и
     * хотят передумать. С заказом всё наоборот: он заводится по одному, за
     * ним звонок врача, и до сохранения его не существует ни для счёта, ни
     * для отчёта. Ждать второго нажатия здесь не за чем.
     *
     * Заодно это чинит два следствия: заказ не «прыгал» в списке при правке
     * (несохранённые записи выстраиваются иначе, чем сохранённые), и номер
     * счёта появлялся только после перезагрузки — обновление с сервера не
     * применяется, пока в разделе есть несохранённые правки.
     */
    void saveNow(tempId);
  };

  /*
   * АВТОСОХРАНЕНИЕ.
   *
   * Правка в журнале уходит на сервер сама, через полсекунды после
   * последнего изменения: пауза собирает серию правок (отметил пять заказов
   * оплаченными) в один пакет, а не шлёт по запросу на каждый щелчок.
   *
   * Пока открыта карточка заказа, автосохранение молчит: там своя кнопка
   * «Применить», и отправлять наполовину заполненный заказ незачем.
   *
   * Молчит оно и при неразобранном расхождении версий: сервер отказывает
   * по той же причине, что и в прошлый раз, и попытки шли бы подряд без
   * конца. Пока пользователь не выбрал в окне разбора, правки ждут.
   */
  useEffect(() => {
    if (!module.dirty || module.saving || openOrder || module.conflicts) return;
    const timer = setTimeout(() => void saveNow(), 500);
    return () => clearTimeout(timer);
    // `saveNow` пересоздаётся на каждом рендере; следим за состоянием правок.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.dirty, module.saving, openOrder, module.conflicts]);

  /** Отправка накопленных правок заказов с разбором отказа. */
  const saveNow = async (newTempId?: string | null) => {
    const outcome = await module.save();
    if (outcome.ok) {
      void invoicesQuery.refetch();
      // Заказ создан — теперь у него есть настоящий ключ, можно предложить письмо.
      const created = newTempId ? outcome.tempIdMap?.[newTempId] : undefined;
      if (created) setLetterQueue((queue) => [...queue, { orderId: created, kind: 'ACCEPTED' }]);
      return;
    }
    if (outcome.blocked) show(outcome.blocked[0]!.message, 'error');
    else if (outcome.error) show(outcome.error, 'error');
  };

  const save = async () => {
    const outcome = await module.save();
    if (outcome.ok) {
      /* Молча: сохранение — не событие, о котором стоит сообщать. */
      /*
        Счета перечитываются вместе с заказами: статус «Оплачено» не хранится,
        а считается по оплате заказов. Без этого отметка оплаты в таблице
        меняла бы значок счёта только после перезагрузки страницы.
      */
      void invoicesQuery.refetch();
    } else if (outcome.blocked) show(outcome.blocked[0]!.message, 'error');
    else if (outcome.error) show(outcome.error, 'error');
  };

  const toggleSelect = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /**
   * Выделенные заказы берутся из ВСЕХ, а не из видимых.
   *
   * Это и есть суть сквозного выделения: пользователь отметил 5 заказов
   * в июле, переключился на август, отметил ещё 10 — и действие применяется
   * ко всем 15. Если фильтровать по видимым, половина выбора тихо пропала бы.
   */
  const selectedOrders = orders.filter((order) => selected.has(order.id));
  const openInvoice = openInvoiceId ? invoices.find((i) => i.id === openInvoiceId) ?? null : null;
  /**
   * Счета, которые уйдут вместе с выделенными заказами.
   *
   * Номера, а не служебные ключи: в предупреждении человек должен узнать
   * документ, который у него на глазах пропадёт.
   */
  const doomedInvoices = [
    ...new Set(
      selectedOrders
        .filter((order) => order.invoiceId)
        .map((order) => order.invoiceNumber ?? invoices.find((i) => i.id === order.invoiceId)?.number)
        .filter((number): number is number => typeof number === 'number'),
    ),
  ].sort((a, b) => a - b);

  /** Деньги по ВЫДЕЛЕННЫМ заказам — не по выборке фильтра, а по отмеченным. */
  /*
    В панели выделения — то же правило. Отметив пачку заказов, среди которых
    есть замороженный, человек видит сумму, которую действительно можно
    выставить: замороженный в счёт всё равно не пойдёт.
  */
  const selectedВДеньгах = selectedOrders.filter((o) => o.status !== 'Ожидание');
  const selectedSumKop = selectedВДеньгах.reduce((sum, o) => sum + o.costKop, 0);
  const selectedDebtKop = selectedВДеньгах.filter((o) => !o.paid).reduce((sum, o) => sum + o.costKop, 0);

  /** Сколько из выбранного не видно при текущем периоде и фильтрах. */
  const hiddenSelected = selectedOrders.length - visible.filter((o) => selected.has(o.id)).length;

  /*
   * Три куска панели действий, которые на телефоне и на широком экране стоят
   * в РАЗНЫХ местах: на телефоне счётчик и крестик уходят отдельной строкой
   * наверх, потому что вместе с пятью кнопками в одну строку они не влезают.
   * Объявлены здесь, чтобы разметка не двоилась и поведение не разъезжалось.
   */
  /**
   * Счётчик выделенного — с деньгами.
   *
   * Одного числа заказов мало: перед «Счёт/акт» первым делом смотрят на сумму,
   * а перед «Оплачено» — на долг. Без них приходилось закрывать панель и
   * складывать строки глазами.
   */
  /*
   * Высота плавающей панели отмеченных — чтобы список под ней не прятался.
   *
   * Панель висит над концом страницы, и последний заказ уходил под неё
   * целиком: отметил всех, прокрутил вниз — а что в последней карточке, не
   * видно, докрутить её выше некуда. Под список подкладывается пустое место
   * ровно высотой панели.
   *
   * Высота МЕРЯЕТСЯ, а не задаётся числом: панель бывает в две строки и в
   * три (с предупреждением «счёт объединить нельзя»), на телефоне и на
   * широком экране разная. Цикла нет — отступ стоит под списком и на размер
   * самой панели не влияет.
   */
  const панельRef = useRef<HTMLDivElement | null>(null);
  const [высотаПанели, setВысотаПанели] = useState(0);
  useEffect(() => {
    const панель = панельRef.current;
    if (!панель) {
      setВысотаПанели(0);
      return;
    }
    const замерить = () => setВысотаПанели(панель.getBoundingClientRect().height);
    замерить();
    if (typeof ResizeObserver === 'undefined') return;
    const наблюдатель = new ResizeObserver(замерить);
    наблюдатель.observe(панель);
    return () => наблюдатель.disconnect();
  }, [selected.size > 0]);

  const counter = (
    /*
      На телефоне всё в одну строку, на широком — в две.
      Двухстрочный блок добавлял панели высоты, а она на телефоне и так
      закрывает треть списка.
    */
    <span className="flex min-w-0 flex-shrink flex-wrap items-baseline gap-x-2 px-2 md:flex-col md:items-start md:px-3">
      <span className="flex items-baseline gap-1.5">
        <span className="tabular text-base font-semibold leading-none text-ink md:text-xl">
          {selected.size}
        </span>
        <span className="text-[11px] text-ink2 md:text-xs">выбрано</span>
      </span>
      <span className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-ink2 md:mt-1 md:text-xs">
        <span className="tabular font-medium text-ink">{formatKop(selectedSumKop)} ₽</span>
        {selectedDebtKop > 0 && (
          <span className="tabular" style={{ color: STATUS_COLORS.danger }}>
            долг {formatKop(selectedDebtKop)} ₽
          </span>
        )}
      </span>
    </span>
  );

  /** Подсказка о заказах, выбранных в другом месяце или скрытых фильтром. */
  const hiddenHint =
    hiddenSelected > 0 ? (
      <span className="flex flex-shrink-0 flex-col justify-center px-1" style={{ maxWidth: 150 }}>
        <span className="text-[10px] leading-tight" style={{ color: 'var(--otc-amber)' }}>
          {hiddenSelected} из них не видно здесь
        </span>
        <button
          onClick={() => {
            setAllMode(true);
            setFilters(EMPTY_FILTERS);
          }}
          className="text-left text-[10px] underline"
          style={{ color: 'var(--otc-accent)' }}
        >
          показать все
        </button>
      </span>
    ) : null;

  const closeButton = (
    <button
      onClick={() => setSelected(new Set())}
      className="flex-shrink-0 rounded-full p-2 hover:bg-black/5"
      title="Снять выделение"
      aria-label="Снять выделение"
    >
      <X size={15} className="text-ink2" />
    </button>
  );

  /**
   * Проверка возможности выставить счёт (§2.4).
   * Условие объединения — только совпадение клиники и юрлица; месяц значения
   * не имеет, поэтому по датам здесь ничего не проверяется.
   */
  /** Сколько фильтров применено — иначе свёрнутые легко забыть. */
  const activeFilters =
    filters.doctor.length +
    filters.clinic.length +
    filters.construction.length +
    filters.status.length +
    filters.pay.length +
    filters.paid.length +
    (filters.due ? 1 : 0) +
    (filters.needsInvoice ? 1 : 0) +
    (filters.query.trim() ? 1 : 0);

  /**
   * Почему по ОДНОМУ заказу счёт выставить нельзя. Пусто — можно.
   *
   * Правила те же, что у полосы выделения (invoiceCheck ниже): наличные
   * оплачиваются на месте, замороженную работу выставлять нельзя, а
   * несохранённого заказа для сервера ещё не существует.
   */
  const счётПоЗаказу = (draft: { id?: string | null; pay: string; status: string; invoiceId?: string | null }): string | null => {
    if (draft.invoiceId) return null;
    if (!draft.id || draft.id.startsWith('tmp_')) return 'Сначала примените изменения — счёт выставляется по сохранённому заказу';
    if (draft.pay !== 'По договору') return 'Счёт выставляется только по заказам по договору';
    if (draft.status === 'Ожидание') return 'Заказ в ожидании — счёт по нему не выставляется';
    return null;
  };

  const invoiceCheck = useMemo(() => {
    if (selectedOrders.length === 0) return { can: false, reason: null as string | null, ids: [] as string[] };

    /*
      Замороженный заказ в счёт не идёт — наравне с наличными и с теми, что
      уже в счёте. Врач остановил работу, и выставлять за неё деньги нельзя,
      пока он её не возобновит.

      Уже выставленный счёт при этом не трогается: заморозили после счёта —
      документ остался как есть, у него свой снимок позиций.
    */
    const eligible = selectedOrders.filter(
      (o) => o.pay === 'По договору' && !o.invoiceId && o.status !== 'Ожидание',
    );
    if (eligible.length === 0) {
      return { can: false, reason: 'Среди выбранных нет заказов по договору без счёта', ids: [] };
    }

    const legalOf = (order: ApiOrder) =>
      data?.clinics.find((c) => c.id === order.clinicId)?.branches.find((b) => b.id === order.branchId)?.legalId;

    if (new Set(eligible.map((o) => o.clinicId)).size > 1 || new Set(eligible.map(legalOf)).size > 1) {
      return { can: false, reason: 'Выбраны заказы разных клиник или юрлиц — счёт объединить нельзя', ids: [] };
    }

    /*
     * Счёт нельзя выставить по НЕСОХРАНЁННОМУ заказу.
     *
     * Новый заказ до сохранения живёт только в браузере, под временным
     * номером с приставкой «tmp_». Сервер о нём не знает и отвечал отказом
     * «Часть выбранных заказов не найдена» — по этой фразе догадаться, что
     * надо просто нажать «Сохранить изменения», невозможно.
     */
    const unsaved = eligible.filter((o) => o.id.startsWith('tmp_'));
    if (unsaved.length > 0) {
      return {
        can: false,
        reason: 'Сначала сохраните заказы — счёт выставляется по сохранённым',
        ids: [],
      };
    }

    const skipped = selectedOrders.length - eligible.length;
    return {
      can: true,
      reason: skipped > 0 ? `${skipped} из выбранных не войдут: наличные, в ожидании или уже в счёте` : null,
      ids: eligible.map((o) => o.id),
    };
  }, [selectedOrders, data]);

  /*
   * Кнопка оплаты работает В ОБЕ СТОРОНЫ.
   *
   * Отметить было чем, а снять — нечем: ошибившись, приходилось открывать
   * каждый заказ по одному. Пока среди выбранных есть хоть один неоплаченный,
   * кнопка отмечает оплату; когда оплачены все — снимает. Так на ней всегда
   * написано то, что произойдёт, и второй кнопки не нужно.
   */
  const allSelectedPaid = selectedOrders.length > 0 && selectedOrders.every((o) => o.paid);

  const bulkTogglePaid = () => {
    const paid = !allSelectedPaid;
    for (const order of selectedOrders) update(order.id, { paid });
    show(
      paid
        ? `Отмечено оплаченными: ${selectedOrders.length}`
        : `Снята оплата: ${selectedOrders.length}`,
    );
    setSelected(new Set());
  };

  const bulkMarkDone = () => {
    for (const order of selectedOrders) update(order.id, { status: 'Готово' });
    show(`Отмечено готовыми: ${selectedOrders.length}`);
    setSelected(new Set());
  };

  const bulkDelete = () => {
    const store = useDrafts.getState();
    for (const order of selectedOrders) store.remove('orders', 'order', order.id);
    show(`Помечено к удалению: ${selectedOrders.length}. Нажмите «Сохранить»`);
    setSelected(new Set());
    setConfirmBulkDelete(false);
  };

  /** Новый заказ. Кнопка стоит в двух местах: в шапке на телефоне и в строке месяца. */
  /**
   * Открыть заказ в карточке.
   *
   * Карточка больше не закрывает журнал, поэтому соседний заказ выбирают,
   * не закрывая текущий. Начатые правки при этом НЕ пропадают: они
   * применяются так же, как это делает кнопка «Применить», — иначе переход
   * по журналу молча стирал бы набранное.
   *
   * Незаведённый новый заказ так перенести нельзя: применять нечего, пока
   * его не создали. Поэтому переход придерживается до решения сотрудника.
   */
  const openExisting = (order: Parameters<typeof toDraft>[0]) => {
    if (openOrder?.isNew) {
      show('Сначала создайте или закройте новый заказ');
      return;
    }
    const pending = panelDraft.current;
    if (openOrder && pending && pending.id === openOrder.draft.id && pending !== openOrder.draft) {
      saveOrder(pending);
    }
    panelDraft.current = null;

    /*
     * Повторный двойной щелчок по ТОМУ ЖЕ заказу карточку ЗАКРЫВАЕТ.
     *
     * Открывают и закрывают её одним и тем же движением — как ящик: ткнул
     * ещё раз и убрал. Прежде повторный щелчок молча пересобирал карточку
     * заново, и выглядело это как «программа не отреагировала».
     *
     * Начатые правки при этом не теряются: они уже применены выше, как и
     * при переходе к соседнему заказу.
     */
    if (openOrder && openOrder.draft.id === order.id) {
      setOpenOrder(null);
      return;
    }

    setOpenOrder({ draft: toDraft(order), isNew: false });
  };

  const createOrder = () => {
    if (!data) return;
    const draft = blankOrder(data.clinics, data.doctors);
    if (!draft) {
      show('Сначала добавьте врача — заказ без него не бывает', 'error');
      return;
    }
    setOpenOrder({ draft, isNew: true });
  };

  if (bootstrap.isLoading || ordersQuery.isLoading || !data) return <Spinner />;

  return (
    <div>
      <SectionHeader
        icon={ClipboardList}
        title="Заказы"
        /*
          Подпись — только на широком экране. «Главный экран — рабочий журнал
          лаборатории» ничего не сообщает тому, кто уже открыл этот экран,
          а на телефоне название раздела и так стоит в верхней полосе: строка
          уходила под повторение, вытесняя из списка заказ.
        */
        right={
          /*
            Порядок кнопок — по частоте и по ходу работы: сперва завести заказ,
            потом сохранить сделанное, и только в самом конце — сводный отчёт.
            Он стоял первым и попадался под руку чаще всех, хотя нужен реже
            всех: его формируют раз в месяц, а заказы заводят каждый день.
          */
          <>
            {/*
              «Создать заказ» стоит ОТДЕЛЬНО от остальных.

              Это главное действие экрана, а в общем ряду через одинаковый
              зазор оно читалось как одна из четырёх равных кнопок. Отступ
              шире, а на широком экране ещё и черта — тем же приёмом в меню
              отделены разделы от выхода.
            */}
            {/*
              Отступ справа — ПОЛОВИНА ушедшего «Сводного отчёта».

              Отчёт уехал в строку месяца, а место за собой оставил: без всякого
              отступа группа кнопок съезжала вправо и «Создать заказ» вставала
              туда, где привычно стоит «Сохранить». Полный отступ возвращал
              кнопку на прежнее место, но между нею и сохранением зияла дыра
              шириной с кнопку. Половина — середина между этими крайностями:
              кнопка недалеко от прежнего места, а ряд не разорван.
            */}
            {/*
              На широком экране кнопка ушла в строку месяца, к «Сводному отчёту»:
              кнопок сохранения рядом больше нет, и держать её одну в шапке
              незачем. На телефоне строка месяца и так тесная — там она здесь.
            */}
            <span className="order-1 mr-2 md:hidden">
              <Button onClick={createOrder}>
                <Plus size={15} /> Создать заказ
              </Button>
            </span>

            {/*
              «Сохранить» и «Отменить» на телефоне — ВТОРОЙ СТРОКОЙ, целиком.

              Кнопка появляется только когда есть что сохранять, и, вставая
              в общий ряд, она вклинивалась между «Создать заказ» и «Сводным
              отчётом» и растаскивала их по трём строкам. Теперь две постоянные
              кнопки всегда стоят рядом и на месте, а появляющаяся приходит
              снизу и ничего не двигает.

              На широком экране порядок прежний — заказ, сохранение, отчёт:
              там места хватает, и переставлять их незачем.
            */}
            {/*
              Правки в журнале сохраняются САМИ.

              Кнопок «Сохранить» и «Отменить» здесь больше нет: статус, оплата
              и прочие отметки меняются на бегу, и несохранённый журнал у одного
              сотрудника означал, что остальные видят устаревшие данные — и
              заводят те же заказы повторно. Осталась короткая надпись о том,
              что правка ушла.
            */}
            <span className="order-3 basis-full text-xs text-ink2 md:order-2 md:basis-auto">
              {module.saving ? 'Сохраняем…' : module.dirty ? 'Изменено' : ''}
            </span>
            {/* На телефоне отчёт остаётся в шапке: строка месяца там и так тесная. */}
            <span className="order-2 md:hidden">
              {/*
                Вид ТОТ ЖЕ, что у кнопки на широком экране: белая плашка,
                рамка, обычный цвет текста.

                Общая «вторичная» кнопка выглядит иначе — одна рамка без
                заливки и приглушённый текст. На бежевом фоне страницы она
                теряла плоскость и читалась как подпись, а не как кнопка,
                хотя на широком экране рядом стоит её белая ровесница.
              */}
              <button
                onClick={() => setReportOrders(null)}
                className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors hover:bg-black/5"
                style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
              >
                <FileText size={15} /> Сводный отчёт
              </button>
            </span>
          </>
        }
      />

      {/* Сводка за период */}
      {/*
        Сводка на телефоне — сетка 3×2, а не перенос по одному числу в строку.
        Переносом она вырастала на пол-экрана, и до первого заказа надо было
        прокручивать; ради этого экран и открывают.

        Отступы и межстрочные интервалы здесь жмутся до предела читаемости:
        сводка — справка, на неё смотрят мельком, а место она занимала как
        три заказа. Числа остались прежнего кегля, ужаты только просветы.
      */}
      {/*
        СВОДКА РАЗДЕЛЕНА НА ДВЕ ЧАСТИ (26.09.2026, по эскизу владельца).

        Слева работа: сколько заказов, сколько в очереди, сколько готово, —
        и полоса состояний прямо под этими числами, потому что объясняет
        именно их. Справа деньги: сумма, долг, расход. Между ними черта.

        Раньше все семь чисел стояли одной лентой, а полоса лежала следом
        восьмым элементом и растягивалась на остаток строки: счётчики и рубли
        читались как один ряд, и полоса будто относилась к деньгам.
      */}
      <div
        /*
          `md:flex-wrap` — страховка на узкий монитор.

          Четыре части в строку помещаются начиная примерно с 1500 точек
          ширины окна. На ноутбуке они не влезали, и дата уезжала за правый
          край карточки — обрезалась на «26 сентя…». С переносом часть просто
          опускается на вторую строку: сводка становится выше, но целой.
        */
        className="mb-2 flex flex-col gap-1.5 rounded-xl border px-2.5 py-1.5 md:mb-4 md:flex-row md:flex-wrap md:items-center md:gap-y-2 md:p-4"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        {/* РАБОТА. На телефоне подписи и числа мельче: сводка занимала треть
            экрана, а смотрят на неё мельком. */}
        {/* Обе части — ПО СОДЕРЖИМОМУ, а не по половине ширины: на широком
            мониторе растянутые до краёв числа расходятся так далеко, что
            сводку приходится читать поворотом головы. Пустое место справа
            здесь уместнее. */}
        {/*
          Левая часть ШИРЕ, чем её числа, — ради полосы под ними.

          По содержимому она выходила около трёхсот точек, и полоса состояний
          получалась короткой: доли в ней различались плохо, а «Ожидание»
          в пару процентов превращалось в незаметную чёрточку. Вдвое длиннее —
          и пропорция читается. Числа остаются прижатыми влево, лишнюю ширину
          забирает только полоса.
        */}
        {/*
          Ширина растёт СТУПЕНЯМИ по ширине окна, а не одним числом.

          Полосе хочется места, но справа у сводки стоят деньги, срочные
          работы и дата — на ноутбуке им вместе тесно, и жёсткие 34rem
          выдавливали дату за правый край карточки. Поэтому на узком окне
          левая часть скромнее, а разворачивается она там, где есть куда.
        */}
        <div className="md:pr-6 min-[1700px]:min-w-[28rem] min-[1900px]:min-w-[34rem]">
          <div className="flex flex-wrap items-start gap-x-4 gap-y-0.5 md:gap-x-8">
            <Stat label="Всего заказов" value={String(stats.total)} />
            <Stat label="В очереди" value={String(stats.queued)} color={QUEUED_COLOR} />
            <Stat label="Готово" value={String(stats.done)} color={STATUS_COLORS.done} />
            {stats.hold > 0 && (
              <Stat label="Ожидание" value={String(stats.hold)} color={STATUS_COLORS.hold} />
            )}
          </div>
          {/* Полоса — БЕЗ подписи под ней: числа над ней уже окрашены в те же
              цвета, и строка «Готово · В очереди» повторяла их словами. */}
          <StatusMix
            total={stats.total}
            done={stats.done}
            queued={stats.queued}
            hold={stats.hold}
            сЛегендой={false}
            className="mt-1.5 md:mt-2.5"
          />
        </div>

        {/* Черта только на широком экране: на телефоне части и так стоят
            друг под другом, и вертикальная линия там не к чему. */}
        <div
          className="hidden self-stretch border-l md:block"
          style={{ borderColor: 'var(--otc-line)' }}
        />

        {/*
          ДЕНЬГИ. «Остаток» отсюда УБРАН (25.09.2026, решение владельца):
          журнал — про работу, а остаток после аренды и налога смотрят
          в «Финансах», где рядом стоят и зарплаты, и прочие расходы.
        */}
        <div className="md:pl-6">
          {/* Деньги НЕ ПЕРЕНОСЯТСЯ: тремя числами в одну строку они читаются
              разом, а перенос ставил «Сумму заказов» отдельной строкой над
              остальными — будто она заголовок, а не такое же число. */}
          <div className="flex flex-wrap items-start gap-x-4 gap-y-0.5 whitespace-nowrap md:flex-nowrap md:gap-x-8">
            <Stat label="Сумма заказов" value={`${formatKop(stats.sumKop)} ₽`} />
            <Stat label="Задолженность" value={`${formatKop(stats.debtKop)} ₽`} color={STATUS_COLORS.danger} />
            <Stat label="Расход" value={`${formatKop(stats.expenseKop)} ₽`} />
          </div>
          {/*
            Полоса ОПЛАТЫ — ровно как полоса работ слева.

            Числа отвечают «сколько должны», но не «много это или мало».
            Долг в 1 332 200 ₽ при сумме 1 663 300 ₽ — это четыре пятых
            неоплаченного месяца, и видно это сразу, а не после деления
            в уме. Расход в полосу не входит: он не часть суммы заказов,
            а отдельная величина — во что эта работа обошлась лаборатории.
          */}
          <ПолосаОплаты
            sumKop={stats.sumKop}
            debtKop={stats.debtKop}
            className="mt-1.5 md:mt-2.5"
          />
        </div>

        {/*
          На телефоне срочные работы и дата стоят В ОДНУ СТРОКУ.

          Порознь они добавляли сводке две строки — почти вдвое к её высоте,
          то есть ещё два заказа, не поместившихся на экран. Подписи
          «Требуют внимания» и «Сегодня» там тоже сняты: кнопка со значком
          часов и календарь с датой называют себя сами.

          `md:contents` на широком экране убирает саму обёртку, и её дети
          снова становятся частями сводки — с чертами между ними.
        */}
        <div className="flex items-center justify-between gap-3 md:contents">
        {/*
          ТРЕУГОЛЬНИК «ЗАБЫТ СЧЁТ» — в пустом месте между деньгами и правым
          краем сводки (место выбрано владельцем 27.09.2026).

          ТОТ ЖЕ значок и то же правило, что в колонке «Счёт»: аппарат готов,
          оплата по договору, счёта нет, а юрлицо просит напоминать. Здесь он
          сводный — сколько таких строк в журнале, — и служит к ним переходом.

          Пропустить счёт легче, чем кажется: заказ уходит врачу, строка
          уезжает вниз списка, и вспоминают о ней в конце месяца по бумагам.

          Значком, а не плашкой: плашек в сводке уже хватает, а тут нужен
          короткий сигнал «посмотри сюда». Нажатие оставляет в журнале ровно
          эти заказы — дальше их отмечают и выставляют счёт кнопкой снизу.

          `md:ml-auto` стоит на нём, а не на черте: так он прижат к правой
          части сводки, а пустое место остаётся слева — ровно там, где было.

          Когда счетов не ждёт никто, значка НЕТ вовсе. Серый треугольник
          «всё хорошо» — противоречие: предупреждение либо есть, либо нет.
        */}
        {треугольникСчетов('hidden md:ml-auto md:inline-flex')}

        {/*
          Черта перед «Требуют внимания». `md:ml-auto` на ней остаётся для
          случая, когда треугольника нет: без него прижимать вправо было бы
          нечем, и срочные работы с датой уехали бы к деньгам.
        */}
        {stats.срочных > 0 && (
          <div
            className={`hidden self-stretch border-l md:block${stats.безСчёта > 0 ? ' md:ml-6' : ' md:ml-auto'}`}
            style={{ borderColor: 'var(--otc-line)' }}
          />
        )}

        {/*
          ТРЕБУЮТ ВНИМАНИЯ — не число, а КНОПКА.

          Сколько заказов горит, полезно знать, но полезнее сразу их увидеть.
          Нажатие ставит отбор «Очень срочно» и «Срочно», повторное — снимает;
          это тот же фильтр, что в списке «Статус изготовления», поэтому
          включённое состояние видно и там.

          Когда срочного НЕТ, плашки нет вовсе — вместе с подписью и чертой.
          Раньше она серела и не нажималась, но место занимала и каждый день
          сообщала «ноль», то есть ничего. Пустая сводка короче на целую
          часть, и глазу не за что цепляться там, где ничего не происходит.
        */}
        {/* Поле справа больше, чем слева: плашка «Срочные работы» шире своей
            подписи и почти упиралась в следующую черту — казалось, что она
            к ней приклеена, а к деньгам слева отношения не имеет. */}
        {stats.срочных > 0 && (
        <div className="md:pl-6 md:pr-8">
          <p className="hidden text-[9px] leading-none text-ink2 md:block md:text-xs md:leading-tight">
            Требуют внимания
          </p>
          <span className="mt-0.5 flex flex-wrap items-center gap-2 md:mt-1">
          <button
            /* Снимает «забыт счёт» по той же причине — см. треугольник. */
            onClick={() => {
              setAllMode(!срочныеВключены);
              setSelectedOnly(false);
              сброситьСчёт();
              setFilters({
                ...EMPTY_FILTERS,
                status: срочныеВключены ? [] : ['Очень срочно', 'Срочно'],
                needsInvoice: false,
              });
            }}
            aria-pressed={срочныеВключены}
            title={
              срочныеВключены
                ? 'Вернуться к выбранному месяцу'
                : 'Показать срочное и очень срочное за все месяцы'
            }
            /*
              ЯНТАРНЫЙ — цвет с эскиза владельца (26.09.2026).

              Это тот же янтарь, что у «В очереди» и у её доли в полосе рядом,
              и в этом есть смысл: срочные работы — как раз та часть очереди,
              за которую берутся первой.
            */
            className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors md:px-3 md:py-1.5 md:text-sm"
            style={{
              borderColor: `color-mix(in srgb, ${QUEUED_COLOR} 55%, transparent)`,
              background: срочныеВключены
                ? `color-mix(in srgb, ${QUEUED_COLOR} 28%, var(--otc-surface))`
                : `color-mix(in srgb, ${QUEUED_COLOR} 14%, var(--otc-surface))`,
              color: QUEUED_COLOR,
            }}
          >
            <Clock size={14} className="flex-shrink-0" />
            <span className="whitespace-nowrap">Срочные работы</span>
            {/* Счётчик кружком — как значок на фильтрах: сколько именно горит. */}
            <span
              className="tabular inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white md:h-5 md:min-w-[1.25rem] md:text-[11px]"
              style={{ background: QUEUED_COLOR }}
            >
              {stats.срочных}
            </span>
          </button>
          </span>
        </div>
        )}

        {/*
          ТРЕУГОЛЬНИК НА ТЕЛЕФОНЕ — своим элементом, а не внутри «Требуют
          внимания»: та часть исчезает, когда срочных работ нет, и значок
          пропадал бы вместе с ней, хотя забытые счета никуда не делись.
        */}
        {треугольникСчетов('inline-flex md:hidden')}

        {/*
          Черта перед датой. «Прижаться вправо» достаётся ей, когда ни
          треугольника, ни срочных работ нет: кто-то должен отодвинуть дату
          в правый угол, а обоих прежних носителей на экране нет — и дата
          прилипала к расходу посреди сводки.
        */}
        <div
          className={`hidden self-stretch border-l md:block${
            stats.безСчёта === 0 && stats.срочных === 0 ? ' md:ml-auto' : ''
          }`}
          style={{ borderColor: 'var(--otc-line)' }}
        />

        {/*
          СЕГОДНЯ — дата словом.

          Сроки сдачи в журнале стоят числами, и «через сколько ещё» считают
          от сегодняшнего дня. Держать его в голове при работе с месяцем,
          который листается стрелками, оказалось лишней нагрузкой: открыт
          август, а сегодня — сентябрь.
        */}
        {/* На телефоне даты НЕТ: сегодняшнее число там и так стоит в строке
            состояния наверху экрана, а место в сводке дороже. */}
        <div className="hidden md:block md:pl-6">
          <p className="hidden text-[9px] leading-none text-ink2 md:block md:text-xs md:leading-tight">
            Сегодня
          </p>
          {/*
            Дата ОТКРЫВАЕТ КАЛЕНДАРЬ, а не просто показывает число.

            Сроки сдачи в журнале стоят числами, и «через сколько ещё» считают
            от сегодняшнего дня; посмотреть, на какой день недели попадает
            пятница через две недели, было негде. Выбранный день переводит
            журнал на его месяц — тем же движением, что стрелки под сводкой,
            только сразу в нужный, без десятка нажатий.

            Календарь тот же, что у полей «Получен» и «Сдача», — свой второй
            однажды разошёлся бы с ним видом.
          */}
          <DateField
            value={null}
            today={today}
            /* «Сегодня · Завтра · Через 3 дня» тут лишние: ими назначают срок
               сдачи, а здесь календарём переходят по месяцам. */
            быстрыеДаты={false}
            onChange={(выбрано) => {
              if (!выбрано) return;
              /*
                Режимы «Все заказы» и «только выбранные» снимаются: в них
                месяц не при чём, и выбор дня остался бы без ответа —
                журнал не шелохнулся бы.
              */
              setAllMode(false);
              setSelectedOnly(false);
              setMonthOffset(смещениеМесяца(today, выбрано));
            }}
            renderTrigger={(открыть, открыт) => (
              <button
                onClick={(e) => открыть(e.currentTarget)}
                title="Открыть календарь · выбранный день переводит журнал на его месяц"
                className="-mx-1 flex items-center gap-1.5 rounded-lg px-1 text-[12px] font-semibold leading-tight text-ink transition-colors hover:bg-black/5 md:mt-1 md:gap-2 md:text-lg md:font-medium"
                style={открыт ? { background: 'color-mix(in srgb, var(--otc-ink) 7%, transparent)' } : undefined}
              >
                <CalendarDays size={15} className="flex-shrink-0 text-ink2 md:h-4 md:w-4" />
                <span className="whitespace-nowrap">{formatDateLong(today)}</span>
              </button>
            )}
          />
        </div>
        </div>
      </div>

      {/* Период и итоги по выборке */}
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5 md:mb-3 md:gap-2">
        <button
          onClick={() => setMonthOffset((m) => m - 1)}
          /* Левее января 2026 заказов нет: программа началась с него. */
          disabled={!byMonth || isFirstMonth(monthKeyForOffset(today, monthOffset))}
          className="rounded-lg border p-1 disabled:opacity-40 md:p-1.5"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <ChevronLeft size={15} className="text-ink2" />
        </button>
        {/*
          `first-letter:uppercase`, а не `capitalize`: последний поднимает
          первую букву КАЖДОГО слова, и месяц печатался как «Август 2026 Г.» —
          с большой «Г» в сокращении «г.».
        */}
        {/*
          Месяц и стрелки — НА БЕЛОМ, как соседние кнопки и поля.

          Раньше вся тройка лежала прямо на фоне страницы и читалась подписью,
          а не переключателем: стрелки терялись, и было неясно, что месяц
          вообще можно сменить.
        */}
        <span
          className="min-w-[7rem] whitespace-nowrap rounded-lg border px-2 py-1 text-center text-xs font-medium first-letter:uppercase md:min-w-[10rem] md:px-3 md:py-1.5 md:text-sm"
          style={{
            color: byMonth ? 'var(--otc-ink)' : 'var(--otc-ink2)',
            borderColor: 'var(--otc-line)',
            background: 'var(--otc-surface)',
          }}
        >
          {invoiceFocus
            ? focusedInvoice
              ? `Счёт №${invoiceNumberText(focusedInvoice.number, focusedInvoice.numberSuffix)}`
              : 'Заказы счёта'
            : selectedOnly
              ? `Отмеченные: ${selected.size}`
              : allMode
                ? 'Все заказы'
                : `${formatMonthKey(month!)} г.`}
        </span>
        <button
          onClick={() => setMonthOffset((m) => m + 1)}
          disabled={!byMonth}
          className="rounded-lg border p-1 disabled:opacity-40 md:p-1.5"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <ChevronRight size={15} className="text-ink2" />
        </button>

        {byMonth && (
          <button
            onClick={async () => {
              const next = pinnedMonth === month ? null : month;
              if (!await pinMonth(next)) show('Не удалось сохранить месяц. Проверьте соединение и повторите', 'error');
            }}
            disabled={pinSaving}
            aria-pressed={pinnedMonth === month}
            aria-label={pinnedMonth === month ? 'Снять закрепление месяца' : 'Закрепить месяц'}
            title={pinnedMonth === month
              ? 'Снять закрепление: при открытии будет текущий месяц'
              : 'Открывать этот месяц на всех устройствах вашего аккаунта'}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border p-1 text-xs font-medium transition-colors disabled:opacity-50 md:px-2.5 md:py-1.5"
            style={{
              borderColor: pinnedMonth === month ? 'color-mix(in srgb, var(--otc-accent) 35%, var(--otc-line))' : 'var(--otc-line)',
              background: pinnedMonth === month ? 'color-mix(in srgb, var(--otc-accent) 10%, var(--otc-surface))' : 'var(--otc-surface)',
              color: pinnedMonth === month ? 'var(--otc-accent)' : 'var(--otc-ink2)',
            }}
          >
            <Pin size={15} className={pinnedMonth === month ? '' : '-rotate-45'} />
            <span className="hidden md:inline">{pinnedMonth === month ? 'Закреплён' : 'Закрепить'}</span>
          </button>
        )}
        {pinnedMonth && pinnedMonth !== month && (
          <button
            onClick={() => {
              setAllMode(false);
              setSelectedOnly(false);
              сброситьСчёт();
              setFilters(EMPTY_FILTERS);
              setMonthOffset(orderMonthOffset(today, pinnedMonth));
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border p-1 text-xs md:px-2.5 md:py-1.5"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)', color: 'var(--otc-accent)' }}
            aria-label={`Перейти к закреплённому месяцу: ${formatMonthKey(pinnedMonth)}`}
            title={`Перейти к закреплённому месяцу: ${formatMonthKey(pinnedMonth)}`}
          >
            <Pin size={15} /> <span className="hidden md:inline">{formatMonthKey(pinnedMonth)}</span>
          </button>
        )}
        {monthOffset !== 0 && byMonth && (
          <button
            onClick={() => setMonthOffset(0)}
            className="rounded-full px-2 py-0.5 text-[11px] md:px-2.5 md:py-1 md:text-xs"
            style={{ background: 'color-mix(in srgb, var(--otc-accent) 12%, var(--otc-surface))', color: 'var(--otc-accent)' }}
          >
            Текущий месяц
          </button>
        )}

        <button
          onClick={() => {
            setAllMode((v) => !v);
            setSelectedOnly(false);
            сброситьСчёт();
          }}
          className="rounded-full border px-2 py-0.5 text-[11px] md:px-2.5 md:py-1 md:text-xs"
          style={{
            borderColor: allMode ? 'var(--otc-accent)' : 'var(--otc-line)',
            color: allMode ? 'var(--otc-accent)' : 'var(--otc-ink2)',
            background: allMode
                ? 'color-mix(in srgb, var(--otc-accent) 12%, var(--otc-surface))'
                : 'var(--otc-surface)',
          }}
        >
          {allMode ? '✓ Все заказы' : 'Все заказы'}
        </button>

        {/*
          «Отмеченные» появляется только когда есть что показывать.

          Пустая кнопка сбивала бы с толку: нажал — список пуст, и непонятно,
          то ли режим сломан, то ли ничего не отмечено.
        */}
        {selected.size > 0 && (
          <button
            onClick={() => {
              setSelectedOnly((v) => !v);
              setAllMode(false);
              сброситьСчёт();
            }}
            className="rounded-full border px-2 py-0.5 text-[11px] md:px-2.5 md:py-1 md:text-xs"
            style={{
              borderColor: selectedOnly ? 'var(--otc-accent)' : 'var(--otc-line)',
              color: selectedOnly ? 'var(--otc-accent)' : 'var(--otc-ink2)',
              background: selectedOnly
                ? 'color-mix(in srgb, var(--otc-accent) 12%, var(--otc-surface))'
                : 'var(--otc-surface)',
            }}
            title="Показать отмеченные заказы всех месяцев на одной странице"
          >
            {selectedOnly ? `✓ Отмеченные: ${selected.size}` : `Отмеченные: ${selected.size}`}
          </button>
        )}

        {invoiceFocus && (
          <button
            onClick={сброситьСчёт}
            className="rounded-full border px-2 py-0.5 text-[11px] md:px-2.5 md:py-1 md:text-xs"
            style={{
              borderColor: 'var(--otc-accent)',
              color: 'var(--otc-accent)',
              background: 'color-mix(in srgb, var(--otc-accent) 12%, var(--otc-surface))',
            }}
            title="Вернуться к помесячному просмотру"
          >
            ✕ Выйти из счёта
          </button>
        )}

        {/*
          Итоги ПО ВЫБОРКЕ — меняются вслед за фильтрами.

          Подписи на телефоне сокращены до слов «расход» и «долг»: полными они
          занимали столько же места, сколько целый заказ.

          Перенос РАЗРЕШЁН, и это важнее вида в одну строку. Запрет переноса
          держал справку одной лентой, а когда суммы доросли до семи знаков,
          лента стала шире экрана — и раздвинула вместе с собой всю страницу:
          заголовок, кнопки и карточки уезжали влево за край, и вернуть их
          можно было только прокруткой вбок. Не переносятся только пары
          «подпись + число», чтобы «долг» не отрывался от суммы.
        */}
        {/*
          «Сводный отчёт» стоит ЗДЕСЬ, перед числами выборки, и только на
          широком экране. Отчёт собирается ровно по той выборке, итоги которой
          показаны справа, — рядом с ними ему и место, а в шапке он стоял
          третьей кнопкой среди действий над заказами.
        */}
        {/*
          «Создать заказ» и «Сводный отчёт» — ПАРОЙ, через еле заметную черту.

          Раньше «Создать заказ» стояла в шапке, отделённая от кнопок сохранения
          широким просветом. Кнопок сохранения не стало — правки уходят сами, —
          и просвет потерял смысл. Кнопка переехала к отчёту: просвет вдвое уже,
          а черта показывает, что действия разные — завести работу и собрать
          по ней документ.
        */}
        <span className="ml-auto hidden items-center gap-6 md:inline-flex">
          <Button onClick={createOrder}>
            <Plus size={15} /> Создать заказ
          </Button>

          <span className="h-5 w-px" style={{ background: 'var(--otc-line)' }} aria-hidden />

          <button
            onClick={() => setReportOrders(null)}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors hover:bg-black/5 md:px-3.5 md:py-2 md:text-sm"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            <FileText size={15} /> Сводный отчёт
          </button>
        </span>

        {/*
          Итоги по выборке — ТОЛЬКО когда выборка отличается от месяца.

          Без фильтров строка считала ровно те же заказы, что и сводка над ней,
          и повторяла её цифры: сумму, расход и долг дважды на одном экране.
          На телефоне это ещё и две лишние строки над списком. Смысл у неё
          появляется, когда список сужен отбором, — тогда она и показывается.

          Итоги по ОТМЕЧЕННЫМ заказам здесь не дублируются: их показывает
          плавающая панель внизу, которая появляется от первой же галочки.
        */}
        {activeFilters > 0 && (
        <>
        {/* Черта и просвет: кнопка — действие, справа от неё справка. */}
        <span
          className="mx-3 hidden h-5 w-px md:block"
          style={{ background: 'var(--otc-line)' }}
          aria-hidden
        />

        <span className="ml-auto flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px] text-ink2 md:ml-0 md:gap-x-3 md:text-xs">
          <span className="whitespace-nowrap">{visible.length} в выборке</span>
          <span className="whitespace-nowrap">
            <span className="hidden md:inline">Сумма: </span>
            <span className="tabular font-medium text-ink">{formatKop(selectionSumKop)} ₽</span>
          </span>
          <span className="whitespace-nowrap">
            <span className="md:hidden">расход </span>
            <span className="hidden md:inline">Расход: </span>
            <span className="tabular font-medium text-ink2">{formatKop(selectionExpenseKop)} ₽</span>
          </span>
          {selectionDebtKop > 0 && (
            <span className="whitespace-nowrap">
              <span className="md:hidden">долг </span>
              <span className="hidden md:inline">Долг по выборке: </span>
              <span className="tabular font-semibold" style={{ color: STATUS_COLORS.danger }}>
                {formatKop(selectionDebtKop)} ₽
              </span>
            </span>
          )}
        </span>
        </>
        )}
      </div>

      {/*
        На телефоне «Фильтры» и масштаб стоят в одной строке.

        Масштаб живёт в панели фильтров, а она на телефоне свёрнута — значит,
        до управления размером было не добраться, пока не развернёшь фильтры.
        Здесь он вынесен наружу и виден всегда.
      */}
      <div className="mb-1.5 flex items-stretch gap-2 md:hidden">
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          className="flex flex-1 items-center justify-between rounded-lg border px-2.5 py-1.5 text-[13px] text-ink2"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
        <span className="flex items-center gap-2">
          <SlidersHorizontal size={15} />
          Фильтры
          {activeFilters > 0 && (
            <span
              className="rounded-full px-1.5 text-[11px] font-medium text-white"
              style={{ background: 'var(--otc-accent)' }}
            >
              {activeFilters}
            </span>
          )}
        </span>
          <ChevronDown
            size={16}
            style={{ transform: filtersOpen ? 'rotate(180deg)' : undefined, transition: 'transform 150ms' }}
          />
        </button>

        <div
          className="flex flex-shrink-0 items-center gap-0.5 rounded-lg border"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <button
            onClick={() => zoom.zoomBy(-ZOOM_STEP)}
            disabled={!zoom.canZoomOut}
            className="rounded-l-lg px-2.5 py-1.5 disabled:opacity-30"
            title="Мельче — на экран влезет больше заказов"
            aria-label="Мельче"
          >
            <Minus size={14} className="text-ink2" />
          </button>
          <button
            onClick={zoom.reset}
            className="tabular w-11 py-1.5 text-center text-xs text-ink2"
            title="Вернуть обычный размер"
          >
            {zoom.zoom}%
          </button>
          <button
            onClick={() => zoom.zoomBy(ZOOM_STEP)}
            disabled={!zoom.canZoomIn}
            className="rounded-r-lg px-2.5 py-1.5 disabled:opacity-30"
            title="Крупнее"
            aria-label="Крупнее"
          >
            <Plus size={14} className="text-ink2" />
          </button>
        </div>
      </div>

      <div
        className={`mb-4 grid-cols-2 items-center gap-2 rounded-lg p-3 md:flex md:flex-wrap ${
          filtersOpen ? 'grid' : 'hidden'
        }`}
        style={{ background: 'var(--otc-surface)', border: '1px solid var(--otc-line)' }}
      >
        {/*
          Поиск ВЫДЕЛЕН тёмно-серым: рамка в цвет приглушённого текста плюс
          лёгкая серая подложка. Поле терялось среди десятка одинаковых
          выпадающих списков, а начинают работу с журналом обычно с него.
        */}
        {/*
          ЛУПА В ПОЛЕ и поле ШИРЕ.

          В 11rem подсказка обрывалась на «Поиск: пациент, вра…», и по чему
          именно ищет журнал, приходилось догадываться. Лупа говорит то же
          самое значком, без единой буквы, — по ней поле находят взглядом
          даже с пустой подсказкой.

          Отступ слева — строчным стилем, а не классом `pl-8`: рядом стоит
          `px-2.5`, и какой из двух победит, решает порядок правил в собранном
          файле, а не разметка.
        */}
        <span className="relative col-span-2 block w-full md:w-72">
          <Search
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--otc-ink2)' }}
          />
          <input
            className="w-full rounded-md border-2 px-2.5 py-1.5 text-xs"
            style={{
              borderColor: 'var(--otc-ink2)',
              /* Поле БЕЛОЕ: выделяет его рамка, а серая заливка делала его
                 похожим на недоступное для ввода. */
              background: 'var(--otc-surface)',
              paddingLeft: '1.85rem',
            }}
            placeholder={подсказкаПоиска('Поиск: пациент, врач, конструкция, № счёта…')}
            value={filters.query}
            onChange={(e) => setFilters({ ...filters, query: e.target.value })}
          />
        </span>
        <MultiSelectFilter
          label="Все врачи"
          options={filterOptions.doctors}
          selected={filters.doctor}
          onChange={(values) => setFilters({ ...filters, doctor: values })}
        />
        <MultiSelectFilter
          label="Все клиники"
          options={filterOptions.clinics}
          selected={filters.clinic}
          onChange={(values) => setFilters({ ...filters, clinic: values })}
        />
        <MultiSelectFilter
          label="Все конструкции"
          options={filterOptions.constructions}
          selected={filters.construction}
          onChange={(values) => setFilters({ ...filters, construction: values })}
        />
        <MultiSelectFilter
          label="Все доп. элементы"
          options={filterOptions.extras}
          selected={filters.extra}
          onChange={(values) => setFilters({ ...filters, extra: values })}
        />
        <MultiSelectFilter
          label="Статус изготовления"
          options={filterOptions.statuses}
          selected={filters.status}
          onChange={(values) => setFilters({ ...filters, status: values })}
        />
        <MultiSelectFilter
          label="Способ оплаты"
          options={filterOptions.pays}
          selected={filters.pay}
          onChange={(values) => setFilters({ ...filters, pay: values })}
        />
        <MultiSelectFilter
          label="Статус оплаты"
          options={filterOptions.paid}
          selected={filters.paid}
          onChange={(values) => setFilters({ ...filters, paid: values })}
        />

        {/*
          ОТБОР ПО СРОКУ СДАЧИ — календарём, а не списком.

          Выглядит он как соседние плашки, но раскрывается календарём: сроков
          в месяце три десятка, и галочками их выбирать — то же листание,
          только в другом окне. Жирным и точкой отмечены дни, на которые
          что-то сдаётся, — пустой день видно ДО нажатия.
        */}
        <DateField
          value={filters.due}
          today={today}
          быстрыеДаты={false}
          clearable
          clearLabel="Любая дата"
          отмеченныеДни={filterOptions.дниСдачи}
          onChange={(дата) => setFilters({ ...filters, due: дата })}
          renderTrigger={(открыть, открыт) => (
            <button
              onClick={(e) => открыть(e.currentTarget)}
              className="inline-flex w-full items-center gap-1.5 overflow-hidden rounded-md border px-2.5 py-1.5 text-xs md:w-auto"
              style={{
                borderColor: filters.due || открыт ? 'var(--otc-accent)' : 'var(--otc-line)',
                color: filters.due ? 'var(--otc-accent)' : 'var(--otc-ink2)',
                background: filters.due
                  ? 'color-mix(in srgb, var(--otc-accent) 10%, var(--otc-surface))'
                  : 'var(--otc-surface)',
              }}
            >
              {/* Тот же приём, что у остальных плашек: невидимый двойник
                  названия держит ширину, чтобы она не прыгала от выбора. */}
              <span className="grid min-w-0 flex-1">
                <span aria-hidden className="invisible col-start-1 row-start-1 hidden whitespace-nowrap md:block">
                  Дата сдачи
                </span>
                <span className="col-start-1 row-start-1 min-w-0 truncate text-left">
                  {filters.due ? formatDate(filters.due) : 'Дата сдачи'}
                </span>
              </span>
              <ChevronDown size={12} className="flex-shrink-0" />
            </button>
          )}
        />
        {/*
          ЧЕРТА между отборами и действиями.

          Слева семь одинаковых выпадающих списков — они СУЖАЮТ выборку.
          Справа две кнопки, которые делают другое: переворачивают порядок и
          отменяют отборы. В сплошном ряду через одинаковый зазор они читались
          как ещё два фильтра, и «Сбросить» нажимали, думая, что раскроют
          список. На телефоне черты нет: там кнопки и так стоят своей строкой.
        */}
        <span className="col-span-2 flex flex-shrink-0 items-center gap-2 md:ml-1 md:border-l md:pl-3"
          style={{ borderColor: 'var(--otc-line)' }}
        >
        {/*
          ПОРЯДОК — кнопкой, а не только щелчком по шапке столбца.

          По столбцам сортировать умеют не все и не сразу, а «сначала новые»
          нужно каждый день: свежие заказы смотрят первыми. Нажатие
          переворачивает порядок заведения и возвращает его, если до того
          сортировали по другому столбцу.
        */}
        <button
          onClick={() =>
            setSort(
              sort.field === 'entered' && sort.dir === 'desc'
                ? { field: 'entered', dir: 'asc' }
                : { field: 'entered', dir: 'desc' },
            )
          }
          aria-pressed={sort.field === 'entered' && sort.dir === 'desc'}
          /*
            БЕЗ РАМКИ И ЗАЛИВКИ — это действие, а не ещё один отбор.

            В рамке кнопка выглядела восьмым выпадающим списком в ряду из семи
            одинаковых, и «Сбросить» нажимали, думая, что раскроют список.
            Голый текст со значком читается как команда, а отделяет его от
            отборов черта слева.
          */
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-ink hover:bg-black/5"
          title="Порядок заведения заказов"
        >
          {/*
            ПОДПИСЬ МЕНЯЕТСЯ ВМЕСТЕ С ПОРЯДКОМ (решение владельца 28.09.2026).

            Раньше она всегда гласила «Сначала новые», и понять по ней, как
            список разложен сейчас, было нельзя — только по птичке рядом.
            Теперь подпись называет то, что на экране: перевернули — и она
            стала «Сначала старые». Птичка осталась вторым признаком: на
            беглый взгляд стрелка заметнее слова.
          */}
          {sort.field === 'entered' && sort.dir === 'desc' ? (
            <>
              Сначала новые
              <ChevronDown size={14} className="flex-shrink-0" />
            </>
          ) : (
            <>
              Сначала старые
              <ChevronUp size={14} className="flex-shrink-0" />
            </>
          )}
        </button>

        {/*
          «Сбросить» — тоже БЕЗ РАМКИ, приглушённым серым.

          Раньше она была светло-красной: единственная отменяющая кнопка
          в ряду одинаковых списков, её выделяли, чтобы находилась глазами.
          Теперь отборы отделены чертой, и красный стал лишним криком —
          сброс фильтра не беда и не потеря данных. Круговая стрелка слева
          узнаётся сама.
        */}
        <button
          onClick={сброситьЖурнал}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-ink2 hover:bg-black/5"
          title="Снять все отборы и вернуть обычный порядок"
        >
          {/* Круговая стрелка против часовой — общепринятый знак возврата
              к исходному состоянию, тот же, что у «Отменить» в программах. */}
          <RotateCcw size={13} className="flex-shrink-0" />
          Сбросить
        </button>
        </span>

        {/*
          Масштаб — и на телефоне тоже.

          Раньше он считался свойством таблицы, а на телефоне вместо неё
          карточки, и управление пряталось. Но потребность там та же и даже
          острее: экран короткий, и уменьшенные карточки — единственный способ
          увидеть за раз десяток заказов, а не три.
        */}
        {/*
          Сброс ширин появляется, только если их меняли: пока таблица
          раскладывается сама, сбрасывать нечего, и кнопка была бы загадкой.
        */}
        {columns.custom && (
          <button
            onClick={columns.reset}
            className="ml-auto hidden rounded-md border px-2.5 py-1.5 text-xs text-ink2 hover:bg-black/5 md:inline-flex"
            style={{ borderColor: 'var(--otc-line)' }}
            title="Вернуть ширину колонок к автоматической"
          >
            Ширина колонок — сбросить
          </button>
        )}

        <div
          className={`hidden items-center gap-0.5 rounded-md border md:flex${columns.custom ? ' ml-2' : ' ml-auto'}`}
          style={{ borderColor: 'var(--otc-line)' }}
        >
          <button
            onClick={() => zoom.zoomBy(-ZOOM_STEP)}
            disabled={!zoom.canZoomOut}
            className="rounded-l-md px-1.5 py-1.5 hover:bg-black/5 disabled:opacity-30"
            title="Мельче — на экран влезет больше заказов"
          >
            <Minus size={13} className="text-ink2" />
          </button>
          <button
            onClick={zoom.reset}
            className="tabular w-11 py-1.5 text-center text-xs text-ink2 hover:bg-black/5"
            title="Вернуть обычный размер"
          >
            {zoom.zoom}%
          </button>
          <button
            onClick={() => zoom.zoomBy(ZOOM_STEP)}
            disabled={!zoom.canZoomIn}
            className="rounded-r-md px-1.5 py-1.5 hover:bg-black/5 disabled:opacity-30"
            title="Крупнее"
          >
            <Plus size={13} className="text-ink2" />
          </button>
        </div>
      </div>

      {/*
        ПОРЯДОК — над списком, когда фильтры свёрнуты.

        «Сначала новые» нужен каждый день, а фильтры на телефоне закрыты:
        ради одного нажатия приходилось разворачивать всю панель. Когда
        панель открыта, кнопка стоит в ней, на своём месте, и здесь не
        повторяется — две одинаковые кнопки рядом сбивают с толку.

        Стоит СНАРУЖИ блока с масштабом, рядом с «Фильтрами»: это орган
        управления журналом, а не его содержимое. Внутри она уезжала вместе
        с карточками — на 80 % мельчала, на 120 % разбухала, хотя соседняя
        кнопка «Фильтры» оставалась прежней.
      */}
      {!filtersOpen && (
        <div className="mb-1.5 flex items-center gap-2 md:hidden">
          <button
            onClick={() =>
              setSort(
                sort.field === 'entered' && sort.dir === 'desc'
                  ? { field: 'entered', dir: 'asc' }
                  : { field: 'entered', dir: 'desc' },
              )
            }
            aria-pressed={sort.field === 'entered' && sort.dir === 'desc'}
            /*
              Здесь рамка ОСТАЁТСЯ, в отличие от таких же кнопок в ряду
              фильтров: там их отделяет черта и соседние кнопки, а тут они
              стоят над списком сами по себе, и без рамки их не отличить
              от заголовка.
            */
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-ink"
            style={{
              borderColor: 'var(--otc-line)',
              background:
                sort.field === 'entered' && sort.dir === 'desc'
                  ? 'color-mix(in srgb, var(--otc-ink) 16%, transparent)'
                  : 'color-mix(in srgb, var(--otc-ink) 7%, transparent)',
            }}
          >
            {/* Подпись меняется вместе с порядком — как у такой же кнопки
                в ряду фильтров. */}
            {sort.field === 'entered' && sort.dir === 'desc' ? (
              <>
                Сначала новые
                <ChevronDown size={14} className="flex-shrink-0" />
              </>
            ) : (
              <>
                Сначала старые
                <ChevronUp size={14} className="flex-shrink-0" />
              </>
            )}
          </button>

          {/*
            «Сбросить» — РЯДОМ, пока фильтры свёрнуты.

            Отборы на телефоне ставят, развернув панель, а снимают уже потом,
            когда панель давно закрыта: посмотрел выборку, и надо вернуть
            журнал целиком. Ради одного нажатия приходилось разворачивать всю
            панель заново. Когда она открыта, кнопка стоит в ней и здесь не
            повторяется — две одинаковые рядом сбивают с толку.
          */}
          <button
            onClick={сброситьЖурнал}
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-ink2"
            style={{
              borderColor: 'var(--otc-line)',
              background: 'color-mix(in srgb, var(--otc-ink) 7%, transparent)',
            }}
          >
            <RotateCcw size={13} className="flex-shrink-0" />
            Сбросить
          </button>
        </div>
      )}

      {/*
        На телефоне журнал — КАРТОЧКАМИ, а не таблицей.

        Четырнадцать колонок помещаются на экран шириной 390 пикселей только
        боковой прокруткой: чтобы увидеть срок и сумму, таблицу надо возить
        вправо, а потом обратно к фамилии. На карточке всё, ради чего в журнал
        и заходят, — сразу перед глазами.

        Таблица никуда не делась: от 768 пикселей показывается она.
      */}
      {/*
        Запас снизу под плавающую панель: без него она накрывала последние
        карточки, и до них нельзя было дотянуться — прокрутка уже кончилась.
      */}
      <div
        className={`space-y-1.5 md:hidden ${selected.size > 0 ? 'pb-28' : ''}`}
        // Масштаб — тот же, что у таблицы на широком экране: это свойство
        // рабочего места, и телефон запоминает своё значение отдельно.
        style={{ zoom: `${zoom.zoom}%` }}
      >
        {/*
          «Выбрать все» на телефоне.

          В таблице такая галочка есть в шапке, а на телефоне вместо таблицы
          карточки — и шапки у них нет. Выделить десяток заказов под счёт
          приходилось по одному.

          Берёт и снимает ТОЛЬКО видимые, как и галочка в таблице: выбор
          в другом месяце не должен пропадать от нажатия здесь.
        */}
        {visible.length > 0 && (
          <label
            className="flex items-center gap-2 px-1 py-0.5 text-[11px] text-ink2"
          >
            <input
              type="checkbox"
              checked={visible.every((order) => selected.has(order.id))}
              onChange={(event) =>
                setSelected((current) => {
                  const next = new Set(current);
                  for (const order of visible) {
                    if (event.target.checked) next.add(order.id);
                    else next.delete(order.id);
                  }
                  return next;
                })
              }
            />
            Выбрать все — {visible.length}{' '}
            {pluralRu(visible.length, ['заказ', 'заказа', 'заказов'])}
          </label>
        )}

        {visible.map((order) => {
          const urgency = urgencyOf(order, today);
          const label = clinicLabel(order);
          const extras = extrasText(order);
          const invoice = order.invoiceId ? invoices.find((i) => i.id === order.invoiceId) : undefined;
          const invoiceNumber = invoice?.number ?? order.invoiceNumber;
          const chosen = selected.has(order.id);
          /* Заказ, чья карточка сейчас открыта: её видно и в списке. */
          const открыт = openOrder?.draft.id === order.id;

          const needsInvoice = needsInvoiceWarning(
            {
              status: order.status,
              pay: order.pay as PayMethod,
              invoiceId: order.invoiceId,
              legacyInvoiceNo: order.legacyInvoiceNo,
              clinicId: order.clinicId,
              branchId: order.branchId,
            },
            domainClinics,
          );

          return (
            <div
              key={order.id}
              className="rounded-xl border p-2.5"
              style={{
                /*
                  Открытый заказ заметнее выделенного: выделение — это
                  «взял под счёт», а открытый правят прямо сейчас, и найти
                  его в списке нужно с одного взгляда.
                */
                borderColor: открыт
                  ? 'var(--otc-accent-dark)'
                  : chosen
                    ? 'var(--otc-accent)'
                    : 'var(--otc-line)',
                borderWidth: открыт ? 2 : undefined,
                background: открыт
                  ? 'color-mix(in srgb, var(--otc-accent) 18%, var(--otc-surface))'
                  : chosen
                    ? 'color-mix(in srgb, var(--otc-accent) 8%, var(--otc-surface))'
                    : 'var(--otc-surface)',
              }}
            >
              <div className="flex items-start gap-2">
                {/*
                  Галочка вынесена отдельной областью с запасом по краям:
                  пальцем в квадратик 13 пикселей не попасть.
                */}
                <button
                  onClick={() =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (next.has(order.id)) next.delete(order.id);
                      else next.add(order.id);
                      return next;
                    })
                  }
                  className="-m-1 flex-shrink-0 p-1"
                  aria-label={chosen ? 'Снять выделение' : 'Выделить заказ'}
                >
                  <input type="checkbox" checked={chosen} readOnly className="pointer-events-none" />
                </button>

                {/* Тело карточки открывает заказ ОДНИМ касанием: двойное на
                    телефоне неудобно, а промахнуться тут не обо что. */}
                <button
                  onClick={() => openExisting(order)}
                  className="min-w-0 flex-1 text-left"
                >
                  {/*
                    В заголовке ВРАЧ, а не пациент. Заказ приходит от врача,
                    и по нему заказ ищут, группируют и выставляют счёт; пациент
                    же часто вовсе не указан — и заголовок «пациент не указан»
                    занимал самую заметную строку карточки, ничего не сообщая.
                  */}
                  <span className="flex items-start justify-between gap-2">
                    <span className="min-w-0 truncate text-[13px] font-semibold text-ink">
                      {doctorName(order.doctorId) || 'Врач не указан'}
                    </span>
                    {/*
                      Расход — ПОД суммой и бледнее её.

                      Отдельной строки в карточке он не заслуживает: это
                      справка, по которой не работают, а сверяются. Приглушённый
                      и вплотную к сумме, он читается как её пояснение, а не как
                      второе число того же веса.
                    */}
                    <span className="flex flex-shrink-0 flex-col items-end leading-tight">
                      <span className="tabular text-[13px] font-semibold text-ink">
                        {formatKop(order.costKop)} ₽
                      </span>
                      <span className="tabular text-[11px] text-ink2 opacity-60">
                        {formatKop(order.expenseKop)} ₽
                      </span>
                    </span>
                  </span>

                  {/*
                    Пациент КРУПНЕЕ номера и клиники.

                    Все трое шли одной бледной строкой, и фамилия — то, по
                    чему заказ ищут глазами и о чём спрашивает врач по
                    телефону, — стояла вровень со служебным номером строки.
                    Теперь она заметно отделена, а номер и клиника остались
                    справкой при ней.
                  */}
                  <span className="mt-px block truncate text-[11px] text-ink2">
                    №{numbering.get(order.id)}
                    {order.patient && (
                      <span className="text-[13px] font-medium text-ink"> · {order.patient}</span>
                    )}
                    {label.clinic && ` · ${label.clinic}`}
                  </span>

                  {/*
                    Изделие — ГЛАВНОЕ в строке, доп. элементы — приписка.

                    Раньше и то и другое шло одним бледным потоком, и название
                    аппарата терялось среди «+ печать модели, + крючки»: чтобы
                    понять, что делаем, приходилось вчитываться. Теперь аппарат
                    набран заметнее прочих строк карточки, а допы — тем же
                    приглушённым тоном, что и остальная справка.

                    Цвета — МИНИАТЮРАМИ СПРАВА, в конце той же строки. Своей
                    строки они не заслуживают (у большинства заказов цвета
                    нет вовсе), а названия цветов на телефоне не помещаются —
                    образец же узнаётся с одного взгляда, врач по нему цвет
                    и выбирал.
                  */}
                  <span className="mt-0.5 flex items-start gap-2">
                    <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-ink">
                      {construction(order.constructionId)?.name ?? ''}
                      {/* Челюсть — при конструкции, до доп. элементов. */}
                      {order.jaw && ` ${jawText(order.jaw)}`}
                      {order.constructionQty > 1 && ` ×${order.constructionQty}`}
                      {extras && (
                        <span className="text-[11px] font-normal text-ink2"> + {extras}</span>
                      )}
                    </span>
                    {order.colors.length > 0 && (
                      <span className="flex flex-shrink-0 items-center gap-1 pt-0.5">
                        {order.colors.map((id) => {
                          const color = data.colors.find((c) => c.id === id);
                          return color ? <Swatch key={id} color={color} size={12} /> : null;
                        })}
                      </span>
                    )}
                  </span>

                  {/*
                    Даты РАЗНОГО ВЕСА: приёмка приглушена, сдача выделена.

                    Обе были одинаково полужирными, и глаз не отличал их друг
                    от друга. Но работают по сроку сдачи — по нему решают, что
                    брать в работу сегодня; дата приёмки нужна изредка, чтобы
                    свериться, когда работу привезли. Стрелка между ними
                    заменяет подписи «получен» и «сдать».
                  */}
                  <span className="mt-px block text-[11px] text-ink2">
                    <span className="tabular opacity-70">{formatDate(order.received)}</span>
                    {order.due && (
                      <>
                        <span className="opacity-70">{' → '}</span>
                        <span
                          className="tabular text-[12px] font-semibold"
                          style={{ color: urgency.overdue ? STATUS_COLORS.critical : 'var(--otc-ink)' }}
                        >
                          {formatDate(order.due)}
                        </span>
                      </>
                    )}
                  </span>
                </button>
              </div>

              {/* Статус и оплата — рабочие кнопки, а не подписи: их жмут чаще
                  всего, и ради них не должно приходиться открывать заказ. */}
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <StatusQuickPicker
                  order={order}
                  today={today}
                  onChange={(patch) => update(order.id, patch)}
                />

                {/* Та же отметка о письме, что и в таблице на широком экране. */}
                <LetterMark
                  sent={lettersOf(order.id)}
                  status={order.status}
                  canNotify={canNotify(order.doctorId)}
                />

                {/*
                  Оплата отмечается и у заказа В СЧЁТЕ.
                  Запрет здесь стоял зря: он отсылал «управлять оплатой через
                  счёт», а такой возможности в счёте не было. Направление на
                  деле обратное — счёт считается оплаченным, когда оплачены
                  входящие в него заказы, и отмечают их по одному.
                */}
                <PaidQuickPicker
                  paid={order.paid}
                  onChange={(paid) => update(order.id, { paid })}
                />

                {order.invoiceId ? (
                  <button
                    onClick={() => setOpenInvoiceId(order.invoiceId)}
                    className="ml-auto inline-flex items-center gap-1.5 text-sm"
                    style={{ color: 'var(--otc-accent)' }}
                  >
                    <span className="tabular underline">№{invoiceNumber ?? '?'}</span>
                    <Mail
                      size={14}
                      color={
                        invoice && invoice.status !== 'Не отправлен'
                          ? STATUS_COLORS.done
                          : STATUS_COLORS.danger
                      }
                    />
                  </button>
                ) : order.legacyInvoiceNo != null ? (
                  <span
                    className="ml-auto inline-flex items-center gap-1.5 text-sm text-ink2"
                    title={`Счёт №${order.legacyInvoiceNo} из прежнего учёта — в программе не создан, документы не отправлены`}
                  >
                    <span className="tabular">№{order.legacyInvoiceNo}</span>
                    <Mail size={14} color={STATUS_COLORS.danger} />
                  </span>
                ) : order.pay === 'Наличные' ? (
                  <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-ink2">
                    <Banknote size={15} />
                    Наличные
                  </span>
                ) : needsInvoice ? (
                  <span
                    className="ml-auto text-xs font-medium"
                    style={{ color: STATUS_COLORS.warning }}
                  >
                    ⚠ нужен счёт
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}

        {visible.length === 0 && <EmptyState text="Нет заказов по заданным условиям" />}
      </div>

      {/* Таблица — от планшета и шире. На телефоне вместо неё карточки. */}
      {/*
        Обёртка нужна для полосы прокрутки, закреплённой у низа экрана.
        Собственная полоса блока с таблицей висит у его нижнего края, то есть
        под сто шестьдесят третьей строкой, и добраться до неё нельзя, не
        долистав месяц до конца. Подробности — в `StickyXScrollbar`.

        Масштаб (`zoom`) остаётся на внутреннем блоке, а не на этой обёртке:
        полоса не должна мельчать вместе с таблицей.
      */}
      <div className="hidden md:block" ref={журналRef}>
      <div
        ref={tableScrollRef}
        /*
          `tabIndex` — ради клавиш. У прокручиваемого блока PageDown, Home и
          End работают, только когда он может получить фокус; без этого они
          обращались бы к странице, которой прокручиваться уже нечем.
        */
        tabIndex={0}
        className="overflow-x-auto rounded-xl border focus:outline-none"
        style={{
          borderColor: 'var(--otc-line)',
          zoom: `${zoom.zoom}%`,
          /*
            Высота делится на масштаб: `zoom` сжимает и её тоже, и при 80 %
            таблица не дотягивалась бы до низа окна на пятую часть.
          */
          maxHeight: высотаЖурнала ? (высотаЖурнала * 100) / zoom.zoom : undefined,
          overflowY: высотаЖурнала ? 'auto' : undefined,
        }}
      >
        {/*
          Минимальная ширина обязательна для узкого экрана.
          
          Без неё браузер честно вписывает четырнадцать колонок в 390
          пикселей: названия конструкций рассыпаются в столбики по слову,
          строка вырастает на пол-экрана, и журнал перестаёт читаться.
          С заданной шириной таблица просто прокручивается вбок — привычно
          и предсказуемо.
        */}
        <table
          ref={tableRef}
          className="w-full text-sm"
          style={{
            borderCollapse: 'collapse',
            minWidth: 1180,
            /*
              Пока ширины не заданы — раскладка по содержимому. Как только
              пользователь потянул хоть одну границу, переходим на жёсткую:
              иначе браузер продолжал бы «улучшать» соседние колонки, и
              заданная вручную ширина не держалась бы.
            */
            tableLayout: columns.custom ? 'fixed' : 'auto',
          }}
        >
          {columns.custom && (
            <colgroup>
              {COLUMN_IDS.map((id) => (
                <col key={id} style={{ width: columns.widths?.[id] }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr style={{ background: 'var(--otc-table-head)' }}>
              {/* Столбец «№» сортируется: это возврат к порядку заведения. */}
              <Th label="№" field="entered" sort={sort} setSort={setSort} id="no" onResize={startResize} />
              {/*
                Колонка галочек — БЕЗ горизонтальных отступов, содержимое по
                центру. Общий отступ в 24 точки шире самой колонки, и галочка
                в шапке уезжала вбок относительно галочек в строках: две
                соседние ячейки с одинаковым содержимым стояли на разной
                вертикали, и это читалось как перекос таблицы.
              */}
              <th
                className="sticky top-0 z-10 w-10 px-0 py-2 text-center"
                style={{ background: 'var(--otc-table-head)' }}
              >
                {visible.length > 0 && (
                  <input
                    type="checkbox"
                    checked={visible.every((o) => selected.has(o.id))}
                    onChange={(e) =>
                      // Добавляем и снимаем ТОЛЬКО видимые: выбор в другом
                      // месяце не должен пропадать от щелчка по этой галочке.
                      setSelected((current) => {
                        const next = new Set(current);
                        for (const order of visible) {
                          if (e.target.checked) next.add(order.id);
                          else next.delete(order.id);
                        }
                        return next;
                      })
                    }
                    title="Выбрать все видимые заказы"
                  />
                )}
              </th>
              <Th label="Врач" field="doctor" sort={sort} setSort={setSort} id="doctor" onResize={startResize} />
              <Th label="Клиника" field="clinic" sort={sort} setSort={setSort} id="clinic" onResize={startResize} />
              <Th label="Конструкция" field="construction" sort={sort} setSort={setSort} id="construction" onResize={startResize} />
              <Th label="Цвет" sort={sort} setSort={setSort} id="color" onResize={startResize} />
              <Th label="Примечания" sort={sort} setSort={setSort} id="notes" onResize={startResize} />
              <Th label="Пациент" field="patient" sort={sort} setSort={setSort} id="patient" onResize={startResize} />
              <Th label="Получен" field="received" sort={sort} setSort={setSort} id="received" onResize={startResize} />
              <Th label="Дата сдачи" field="due" sort={sort} setSort={setSort} id="due" onResize={startResize} />
              <Th label="Статус" field="urgency" sort={sort} setSort={setSort} id="status" onResize={startResize} />
              <Th label="Стоимость" field="cost" sort={sort} setSort={setSort} id="cost" onResize={startResize} />
              <Th label="Расход" field="expense" sort={sort} setSort={setSort} id="expense" onResize={startResize} />
              <Th label="Оплата" field="paid" sort={sort} setSort={setSort} id="paid" onResize={startResize} />
              <Th label="Счёт" sort={sort} setSort={setSort} id="invoice" onResize={startResize} />
            </tr>
          </thead>
          <tbody>
            {visible.map((order, index) => {
              const urgency = urgencyOf(order, today);
              const label = clinicLabel(order);
              const extras = extrasText(order);
              const invoice = order.invoiceId ? invoices.find((i) => i.id === order.invoiceId) : undefined;
              const invoiceNumber = invoice?.number ?? order.invoiceNumber;
              const sent = invoice ? invoice.status !== 'Не отправлен' : false;

              const needsInvoice = needsInvoiceWarning(
                {
                  status: order.status,
                  pay: order.pay as PayMethod,
                  invoiceId: order.invoiceId,
                  legacyInvoiceNo: order.legacyInvoiceNo,
                  clinicId: order.clinicId,
                  branchId: order.branchId,
                },
                domainClinics,
              );

              const open = () => openExisting(order);
              /*
               * Панель открывается ДВОЙНЫМ щелчком, как строка таблицы в любой
               * учётной программе. По одиночному она распахивалась от любого
               * промаха мимо галочки или значка статуса.
               *
               * `select-none` обязателен: двойной щелчок по тексту выделяет
               * слово, и вместе с открытой панелью оставалась подсвеченная
               * фамилия пациента.
               */
              /*
                Отступ ячейки — 24 точки по краям, то есть 48 между соседними
                колонками. Прежние 12 давали 24: колонки читались как один
                сплошной текст, и взгляд не находил, где кончается конструкция
                и начинается цвет.
              */
      /*
       * Ячейка не даёт содержимому вылезать за свои границы: колонки тянут
       * руками, и в узкой колонке текст наезжал на соседнюю.
       */
      const cell = 'px-6 py-2.5 cursor-pointer select-none overflow-hidden';
              /*
               * Длинные значения — В ОДНУ СТРОКУ с многоточием.
               *
               * «Аппарат RPE Haas M.Rosa с винтом Hyrax© 3D лазерное
               * спекание» занимал шесть строк и раздувал всю строку журнала.
               * Полное название всегда доступно: заказ открывается двойным
               * щелчком, а под курсором работает всплывающая подсказка.
               */
              const clip = `${cell} max-w-[15rem] truncate`;
              /*
                Конструкции — БОЛЬШЕ места, клинике — меньше.

                Названия клиник длинные («Клиника экспертной ортодонтии Татьяны
                Спасич»), и ячейка растягивалась под них, отбирая ширину
                у конструкции. Между тем в конструкции читают не только
                название, но и строку доп. элементов под ним — а она обрывалась
                на «Добавление искусстве…». Клиника теперь тоже обрезается,
                полное название остаётся в подсказке под курсором.
              */
              const clinicClip = cell;
              /*
                Конструкция забирает ВЕСЬ запас ширины: `w-full` у ячейки
                таблицы с автоматической раскладкой означает «отдай мне всё,
                что осталось».

                Без этого на широком мониторе таблица оказывалась уже окна,
                и браузер раздавал лишнее место всем колонкам поровну — между
                фамилией врача и названием клиники появлялся просвет в палец
                шириной, а строка доп. элементов всё равно обрывалась.

                Ширина колонки идёт ЗА ТЕКСТОМ, без верхнего предела: полное
                название конструкции и список доп. элементов читают прямо
                в журнале, и обрезанные они бесполезны. Таблица от этого шире
                окна — она прокручивается вбок, а рядом с фильтрами есть
                управление масштабом.
              */
              const constructionClip = cell;

              return (
                <tr
                  key={order.id}
                  /*
                    Фон — КЛАССАМИ, а не инлайн-стилем: инлайн перебивает :hover,
                    и подсветка при наведении не появлялась. Сами правила
                    и объяснение трёх состояний — в index.css.
                  */
                  className={[
                    'otc-row',
                    selected.has(order.id) ? 'otc-row-selected' : index % 2 ? 'otc-row-alt' : '',
                    // Открытая карточка помечает свою строку — см. `.otc-row-open`.
                    openOrder?.draft.id === order.id ? 'otc-row-open' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  // Подсказка на всей строке: иначе как открыть заказ — не видно.
                  // Ячейки со своими кнопками показывают собственную подпись.
                  title="Двойной щелчок — открыть заказ"
                  style={{ borderTop: '1px solid var(--otc-line)' }}
                >
                  <td className={`${cell} tabular text-xs text-ink2`} onDoubleClick={open}>
                    {numbering.get(order.id)}
                  </td>
                  <td className="px-0 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(order.id)}
                      onChange={() => toggleSelect(order.id)}
                      onClick={(e) => e.stopPropagation()}
                      title="Выбрать заказ"
                    />
                  </td>

                  {/*
                    Фамилия врача не переносится. «Ван-Тин С.В.» ломалось на
                    «Ван-Тин» и «С.В.» — заголовок строки читался как две
                    разные записи, и строка вырастала до полутора высот.
                  */}
                  {/*
                    Врач — ПОЛУЖИРНЫМ: это заголовок строки. По нему заказ ищут
                    глазами, группируют и сверяют с нарядом, а клиника рядом —
                    лишь справка при нём, поэтому она приглушена. Так же
                    устроена карточка заказа на телефоне.
                  */}
                  <td className={`${cell} whitespace-nowrap font-semibold text-ink`} onDoubleClick={open}>
                    {doctorName(order.doctorId) || <span className="font-normal text-ink2">Не указан</span>}
                  </td>

                  <td
                    className={clinicClip}
                    onDoubleClick={open}
                    title={label.branch ? `${label.clinic} · ${label.branch}` : label.clinic}
                  >
                    {/*
                      Клиника ПРИГЛУШЕНА — справка при враче, как в карточках на
                      телефоне. Чёрной она стояла вровень с фамилией врача, и в
                      строке из четырнадцати колонок глазу не за что было
                      зацепиться: всё одного веса.
                    */}
                    {/* Размер — как у примечаний и доп. элементов: вся справка
                        в строке одного вида, мельче и бледнее главного. */}
                    <span className="block truncate text-xs text-ink2">{label.clinic}</span>
                    {label.branch && (
                      <span className="block truncate text-xs text-ink2">{label.branch}</span>
                    )}
                  </td>

                  <td
                    className={constructionClip}
                    onDoubleClick={open}
                    title={
                      extras
                        ? `${construction(order.constructionId)?.name ?? ''} + ${extras}`
                        : (construction(order.constructionId)?.name ?? '')
                    }
                  >
                    <div>
                    {/*
                      Название и количество — в одной строке, обрезается только
                      название. Количество объясняет цену, и уехать под текст
                      оно не должно: «×2» отдельной строкой читается как часть
                      списка доп. элементов.
                    */}
                    <span className="flex items-baseline gap-1.5">
                      {/* Конструкция не выбрана — ячейка пустая, как у клиники. */}
                      <span className="min-w-0 truncate text-ink">
                        {construction(order.constructionId)?.name ?? ''}
                      </span>
                      {/*
                        Челюсть — при САМОЙ конструкции, до количества и доп.
                        элементов: это свойство аппарата, а не заказа. В конце
                        строки она читалась бы как относящаяся к последнему
                        доп. элементу.
                      */}
                      {order.jaw && (
                        <span className="flex-shrink-0 text-ink">{jawText(order.jaw)}</span>
                      )}
                      {/*
                        Количество показывается только когда аппаратов больше
                        одного: «×1» в каждой строке был бы шумом.
                      */}
                      {order.constructionQty > 1 && (
                        <span className="tabular flex-shrink-0 text-xs font-medium text-ink2">
                          ×{order.constructionQty}
                        </span>
                      )}

                    </span>
                    {/*
                      Доп. элементы видны прямо в списке: они влияют на цену.
                      Строка ОДНА: перенос на вторую растил бы высоту строки
                      журнала вдвое ради перечисления, которое читают мельком.
                    */}
                    {extras && (
                      <span className="block truncate text-xs text-ink2">+ {extras}</span>
                    )}
                    </div>
                  </td>

                  <td className={`${cell} overflow-hidden`} onDoubleClick={open} title={colorNames(order.colors) || undefined}>
                    <div className="flex items-center gap-1.5">
                      <span className="min-w-0 truncate text-xs text-ink2">{colorNames(order.colors) || '—'}</span>
                      {order.colors.map((id) => {
                        const color = data.colors.find((c) => c.id === id);
                        return color ? <Swatch key={id} color={color} size={11} /> : null;
                      })}
                    </div>
                  </td>

                  <td
                    className={`${cell} truncate text-xs`}
                    onDoubleClick={open}
                    title={order.notes || undefined}
                    style={{ color: order.notes ? 'var(--otc-ink2)' : 'var(--otc-line)' }}
                  >
                    {order.notes ? (order.notes.length > 26 ? `${order.notes.slice(0, 26)}…` : order.notes) : '—'}
                  </td>

                  {/*
                    Фамилия пациента ОБРЕЗАЕТСЯ. Колонки таблицы тянут за границы,
                    и в узкой колонке длинная фамилия вылезала за край ячейки —
                    накладываясь на соседнюю дату.
                  */}
                  <td className={`${cell} truncate text-ink`} onDoubleClick={open} title={order.patient || undefined}>
                    {order.patient}
                  </td>
                  {/*
                    Даты — НАСЫЩЕННЫМ цветом, размер прежний.
                    
                    По ним работают: получен и сдать. Они были набраны тем же
                    приглушённым серым, что и второстепенные подписи, и на
                    светлом фоне бледнели. Теперь обе основным цветом и
                    полужирные: по ним ведут работу, а не сверяются изредка.
                  */}
                  {/*
                    Дата не переносится. «25-08-2026» — десять знаков, и при
                    ширине окна ноутбука браузер ломал их на две строки: «25-08-»
                    и «2026». Строка заказа от этого вырастала в полтора раза,
                    и на экран влезало на треть меньше заказов. Колонка просит
                    себе ровно ширину даты, остальное таблица разбирает сама.
                  */}
                  {/*
                    Приёмка ПРИГЛУШЕНА, а срок сдачи выделен: работают по
                    сроку, а дату получения открывают, только чтобы свериться.
                    Прежде обе колонки были одинаково полужирными и чёрными —
                    и в строке из четырнадцати колонок они сливались.
                  */}
                  <td className={`${cell} figures whitespace-nowrap text-xs text-ink2`} onDoubleClick={open}>
                    {formatDate(order.received)}
                  </td>
                  <td
                    className={`${cell} figures whitespace-nowrap text-xs font-semibold`}
                    onDoubleClick={open}
                    style={{ color: urgency.overdue ? STATUS_COLORS.critical : 'var(--otc-ink)' }}
                  >
                    {formatDate(order.due)}
                  </td>

                  {/* Статус, оплата и счёт — те же правила обрезки, что у прочих колонок. */}
                  <td className="overflow-hidden px-6 py-2.5">
                    <span className="inline-flex max-w-full items-center gap-1.5 overflow-hidden">
                      <StatusQuickPicker
                        order={order}
                        today={today}
                        onChange={(patch) => update(order.id, patch)}
                      />
                      <LetterMark
                        sent={lettersOf(order.id)}
                        status={order.status}
                        canNotify={canNotify(order.doctorId)}
                      />
                    </span>
                  </td>

                  {/* Деньги не переносятся: «17 500 ₽» не должно ломаться на две строки. */}
                  <td className={`${cell} tabular whitespace-nowrap text-ink`} onDoubleClick={open}>
                    {formatKop(order.costKop)} ₽
                  </td>

                  {/*
                    Расход — сразу после стоимости: их читают парой, разница
                    между ними и есть заработок по заказу. Приглушён серым:
                    это справка, а не то, что предъявляют клинике.
                  */}
                  <td className={`${cell} tabular whitespace-nowrap text-ink2`} onDoubleClick={open}>
                    {formatKop(order.expenseKop)} ₽
                  </td>

                  <td className="overflow-hidden px-6 py-2.5">
                    {/*
                      Оплата выбирается из списка, а не переключается нажатием.
                      Одно касание мимо меняло деньги молча — и заметить это
                      можно было только при сверке с клиникой.
                    */}
                    <PaidQuickPicker
                      paid={order.paid}
                      onChange={(paid) => update(order.id, { paid })}
                    />
                  </td>

                  <td className="overflow-hidden px-6 py-2.5">
                    {order.invoiceId ? (
                      /*
                        Номер счёта открывает сам счёт. Раньше он был подчёркнут,
                        как ссылка, но никуда не вёл: чтобы посмотреть документ,
                        приходилось уходить в «Документы» и искать его там.
                      */
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenInvoiceId(order.invoiceId);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-colors hover:bg-black/5"
                        title={`Открыть счёт №${invoiceNumber ?? '?'} · ${sent ? 'документы отправлены' : 'документы не отправлены'}`}
                      >
                        <span className="tabular text-sm underline" style={{ color: 'var(--otc-accent)' }}>
                          №{invoiceNumber ?? '?'}
                        </span>
                        <Mail size={14} color={sent ? STATUS_COLORS.done : STATUS_COLORS.danger} />
                      </button>
                    ) : order.legacyInvoiceNo != null ? (
                      /*
                        Номер счёта из ПРЕЖНЕГО учёта — переносы июля и августа.

                        Выглядит намеренно не так, как рабочий номер: приглушённый,
                        без подчёркивания, не нажимается. Открывать нечего — счёта
                        в программе нет, есть только его номер из старой таблицы.
                        Конверт красный: документы по нему из программы не уходили.
                      */
                      <span
                        className="inline-flex items-center gap-1.5 text-sm text-ink2"
                        title={`Счёт №${order.legacyInvoiceNo} из прежнего учёта — в программе не создан, документы не отправлены`}
                      >
                        <span className="tabular">№{order.legacyInvoiceNo}</span>
                        <Mail size={14} color={STATUS_COLORS.danger} />
                      </span>
                    ) : order.pay === 'Наличные' ? (
                      /*
                        Наличный заказ и заказ без счёта — РАЗНЫЕ состояния,
                        а показывались одинаковым прочерком.
                        
                        Прочерк читается как «счёт ещё не выставлен», то есть
                        как незакрытое дело. По наличному заказу счёта не будет
                        никогда: клиника платит на месте. Отметка спокойная,
                        приглушённая — это норма, а не то, что надо исправить.
                      */
                      <span
                        className="inline-flex items-center gap-1.5 text-xs text-ink2"
                        title="Оплата наличными — счёт не выставляется"
                      >
                        <Banknote size={15} />
                        Наличные
                      </span>
                    ) : needsInvoice ? (
                      // Вычисляемый индикатор §2.8 — нигде не хранится.
                      <span
                        title="Заказ готов, по договору — нужно оформить счёт"
                        style={{ color: STATUS_COLORS.warning, fontSize: 20, lineHeight: 1 }}
                      >
                        ⚠
                      </span>
                    ) : (
                      // Заказ по договору, но ещё не готов: счёт впереди.
                      <span className="text-xs" style={{ color: 'var(--otc-line)' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}

            {visible.length === 0 && (
              <tr>
                <td colSpan={14}>
                  <EmptyState text="Нет заказов по заданным условиям" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
        <StickyXScrollbar targetRef={tableScrollRef} />
      </div>

      {/*
        Окно предложения письма. Показывается по одному, даже если поводов
        накопилось несколько: два похожих окна подряд — верный способ
        отправить не то, не глядя.
      */}
      {letterAsk && (
        <SendLetterDialog
          orderId={letterAsk.orderId}
          kind={letterAsk.kind}
          doctorName={letterAsk.doctorName}
          toEmail={letterAsk.toEmail}
          letter={letterAsk.letter}
          onDone={(sentTo) => {
            show(`Письмо отправлено на ${sentTo}`);
            // Отметка зеленеет сразу; перезапрос лишь подтверждает.
            setJustSent((list) => [...list, { orderId: letterAsk.orderId, kind: letterAsk.kind }]);
            setLetterQueue((queue) => queue.slice(1));
            void ordersQuery.refetch();
          }}
          onSkip={() => setLetterQueue((queue) => queue.slice(1))}
        />
      )}

      {/* Место под плавающей панелью: без него последний заказ прячется под ней. */}
      {selected.size > 0 && высотаПанели > 0 && (
        <div aria-hidden style={{ height: высотаПанели + 24 }} />
      )}

      {/* Плавающая панель действий над выделенными */}
      {selected.size > 0 && (
        <div
          ref={панельRef}
          className="fixed bottom-3 left-2 right-2 z-30 rounded-2xl shadow-2xl md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2"
          style={{ background: 'var(--otc-surface)', border: '1px solid var(--otc-line)' }}
        >
          {confirmBulkDelete ? (
            <div className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="text-sm text-ink">
                Удалить {selected.size} заказ(ов)?
                {/*
                  Про счета сказано ДО нажатия. Удаление заказа уносит его счёт,
                  и узнать об этом после — значит узнать слишком поздно.
                */}
                {doomedInvoices.length > 0 && (
                  <span className="block text-xs" style={{ color: STATUS_COLORS.danger }}>
                    Вместе с ними {doomedInvoices.length === 1 ? 'удалится счёт' : 'удалятся счета'}{' '}
                    № {doomedInvoices.join(', № ')}
                  </span>
                )}
              </span>
              <button
                onClick={bulkDelete}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-white"
                style={{ background: 'var(--otc-coral)' }}
              >
                Да, удалить
              </button>
              <button
                onClick={() => setConfirmBulkDelete(false)}
                className="rounded-lg border px-3 py-1.5 text-sm text-ink2"
                style={{ borderColor: 'var(--otc-line)' }}
              >
                Отмена
              </button>
            </div>
          ) : (
            <div>
              {invoiceCheck.reason && (
                <p className="px-3 pb-0.5 pt-1.5 text-[11px] leading-snug md:px-4 md:pt-2 md:text-xs" style={{ color: 'var(--otc-coral)' }}>
                  {invoiceCheck.reason}
                </p>
              )}
            <div className="px-2 py-2">
              {/*
                Телефон: счётчик и закрытие — отдельной строкой сверху, кнопки
                под ними. В одну строку они не помещаются никогда: пять
                подписей плюс счётчик шире любого телефона, и панель резалась
                краями экрана — «Удалить» и крестик были не видны и не нажимались.
              */}
              <div className="flex items-center gap-2 px-1.5 pb-1 md:hidden">
                {counter}
                {hiddenHint}
                <span className="ml-auto">{closeButton}</span>
              </div>

              <div className="flex items-center justify-center md:justify-start">
                <span className="hidden md:flex">{counter}</span>
                <span className="hidden md:flex">{hiddenHint}</span>
                <div className="my-1 hidden w-px self-stretch md:block" style={{ background: 'var(--otc-line)' }} />

                <SelectionActionButton
                  icon={FileText}
                  label="Отчёт"
                  color="var(--otc-ink2)"
                  onClick={() => setReportOrders(selectedOrders.map((o) => o.id))}
                />
                <SelectionActionButton
                  icon={Send}
                  label="Счёт/акт"
                  color="var(--otc-accent)"
                  disabled={!invoiceCheck.can}
                  title={invoiceCheck.reason ?? undefined}
                  onClick={() => setInvoiceOrders(invoiceCheck.ids)}
                />
                <SelectionActionButton
                  /* Кредитка, а не доллар: тот же значок, что у такой же
                     кнопки в «Документах» — одно действие, один знак. */
                  icon={CreditCard}
                  label={allSelectedPaid ? 'Снять оплату' : 'Оплачено'}
                  color={allSelectedPaid ? 'var(--otc-ink2)' : STATUS_COLORS.done}
                  title={
                    allSelectedPaid
                      ? 'Все выбранные оплачены — снять отметку'
                      : 'Отметить выбранные оплаченными'
                  }
                  onClick={bulkTogglePaid}
                />
                <SelectionActionButton icon={Check} label="Готово" color={STATUS_COLORS.done} onClick={bulkMarkDone} />
                <SelectionActionButton
                  icon={Trash2}
                  label="Удалить"
                  color="var(--otc-coral)"
                  onClick={() => setConfirmBulkDelete(true)}
                />

                <div className="my-1 hidden w-px self-stretch md:block" style={{ background: 'var(--otc-line)' }} />
                <span className="hidden md:block">{closeButton}</span>
              </div>
            </div>
            </div>
          )}
        </div>
      )}

      {openOrder && (
        <OrderPanel
          // Переход к другому заказу собирает карточку заново: её поля
          // берутся из черновика один раз, при открытии.
          key={openOrder.draft.id}
          order={openOrder.draft}
          onDraftChange={(draft) => {
            panelDraft.current = draft;
          }}
          isNew={openOrder.isNew}
          clinics={data.clinics}
          doctors={data.doctors}
          constructions={data.constructions}
          colors={data.colors}
          categories={data.categories}
          onSave={saveOrder}
          letterState={letterStateFor(openOrder)}
          onSendLetter={(kind) => {
            // Панель закрывается: письмо показывается своим окном поверх, и
            // две панели одна на другой читались бы как одно окно.
            setOpenOrder(null);
            setLetterQueue((queue) => [...queue, { orderId: openOrder.draft.id, kind }]);
          }}
          onClose={() => setOpenOrder(null)}
          /*
           * Документы по одному заказу — прямо из карточки. Те же окна, что
           * открывает полоса выделения в журнале: список заказов у них один
           * и тот же, здесь он просто из одного заказа.
           */
          onInvoice={
            openOrder.isNew || openOrder.draft.invoiceId
              ? undefined
              : () => setInvoiceOrders([openOrder.draft.id!])
          }
          onOpenInvoice={
            openOrder.draft.invoiceId ? () => setOpenInvoiceId(openOrder.draft.invoiceId!) : undefined
          }
          onReport={openOrder.isNew ? undefined : () => setReportOrders([openOrder.draft.id!])}
          invoiceHint={счётПоЗаказу(openOrder.draft)}
          onDelete={
            openOrder.isNew
              ? undefined
              : () => {
                  useDrafts.getState().remove('orders', 'order', openOrder.draft.id);
                  setOpenOrder(null);
                  // Удаление из панели тоже применяется сразу — как и правка.
                  void saveNow();
                  show('Заказ удалён');
                }
          }
        />
      )}

      {invoiceOrders && (
        <InvoiceCreateModal
          orderIds={invoiceOrders}
          orders={orders}
          bootstrap={data}
          onClose={() => setInvoiceOrders(null)}
          onCreated={({ id, number }) => {
            setInvoiceOrders(null);
            setSelected(new Set());
            show(`Счёт №${number} сформирован`);
            void ordersQuery.refetch();
            void invoicesQuery.refetch();
            /*
             * Сразу открываем созданный счёт. Оттуда его отправляют на почту,
             * и закрывать окно значило бы отправить человека искать в списке
             * то, что он только что сделал.
             */
            setOpenInvoiceId(id);
          }}
        />
      )}

      {/* Счёт, открытый из строки заказа: то же окно, что в «Документах». */}
      {openInvoice && (
        <InvoiceDetailModal
          invoice={openInvoice}
          bootstrap={data}
          onClose={() => setOpenInvoiceId(null)}
          onChanged={(message, держатьОткрытым) => {
            if (message) show(message);
            void invoicesQuery.refetch();
            void ordersQuery.refetch();
            if (!держатьОткрытым) setOpenInvoiceId(null);
          }}
          onDeleted={(message) => {
            show(message);
            void invoicesQuery.refetch();
            void ordersQuery.refetch();
            setOpenInvoiceId(null);
          }}
        />
      )}

      {reportOrders !== undefined && (
        <SummaryReportModal
          orders={orders}
          bootstrap={data}
          presetOrderIds={reportOrders}
          onClose={() => setReportOrders(undefined)}
          onSaved={(message) => {
            setReportOrders(undefined);
            setSelected(new Set());
            show(message);
          }}
          onNotify={show}
        />
      )}

      {module.conflicts && (
        <ConflictModal
          conflicts={module.conflicts}
          onReload={() => {
            module.discard();
            module.dismissConflicts();
            void ordersQuery.refetch();
          }}
          onKeepMine={() => void module.keepMine()}
          onClose={module.dismissConflicts}
        />
      )}

      {duplicate && (
        <ConfirmDialog
          title="Такой заказ уже есть"
          description="Совпали пациент, врач, стоимость и дата получения. Возможно, его только что завёл кто-то другой."
          details={
            <ul className="space-y-1">
              {duplicate.found.map((item) => (
                <li key={item.id}>
                  {item.patient}
                  {item.doctorName && ` · ${item.doctorName}`}
                  {item.constructionName && ` · ${item.constructionName}`}
                  {` · ${formatKop(item.costKop)} ₽ · получен ${formatDate(item.received)} · ${item.status}`}
                </li>
              ))}
            </ul>
          }
          confirmLabel="Всё равно создать"
          tone="accent"
          onConfirm={() => {
            const pending = duplicate.draft;
            setDuplicate(null);
            void submitOrder(pending, true);
          }}
          onCancel={() => setDuplicate(null)}
        />
      )}

      {confirmDiscard && (
        <ConfirmDialog
          title="Отменить несохранённые изменения?"
          description="Заказы вернутся к тому виду, в каком они сохранены на сервере."
          details="Отменённые правки не восстанавливаются."
          confirmLabel="Да, отменить"
          onConfirm={() => {
            module.discard();
            setConfirmDiscard(false);
            show('Изменения отменены');
          }}
          onCancel={() => setConfirmDiscard(false)}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Цвет «в очереди» — тот же, что у значка «В работе» в строке таблицы.
 *
 * Раньше здесь стоял оранжевый `warning`, который в самой таблице не
 * встречается ни разу: он означает несохранённые правки. Дробить полоску
 * на «Срочно» и «Очень срочно» тоже незачем — срочный заказ всё равно в
 * очереди, а полоска отвечает на один вопрос: сколько сделано.
 */
const QUEUED_COLOR = STATUS_COLORS.normal;

/**
 * Отметка о письме врачу рядом со статусом.
 *
 * Конверт С ГАЛОЧКОЙ, а не обычный: обычным в соседней колонке помечается
 * отправка счёта, и два разных смысла под одним значком путались бы.
 *
 * Зелёный — уместное сейчас письмо ушло. Красный — ещё нет. Врачу, который
 * от уведомлений отказался или не оставил почты, не показывается ничего:
 * напоминать о том, чего не будет, незачем.
 */
function LetterMark({
  sent,
  status,
  canNotify,
}: {
  sent: ('ACCEPTED' | 'READY')[];
  status: string;
  /** У врача есть почта и он не отказался от уведомлений. */
  canNotify: boolean;
}) {
  if (!canNotify) return null;

  const due = letterDue(status);
  const done = (sent ?? []).includes(due);
  const label = due === 'ACCEPTED' ? 'о приёме' : 'о готовности';
  const title = done
    ? `Врачу отправлено письмо ${label}`
    : `Письмо ${label} врачу ещё не отправлено`;

  return (
    <span className="inline-flex flex-shrink-0 items-center" title={title} aria-label={title}>
      {done ? (
        <MailCheck size={15} style={{ color: STATUS_COLORS.done }} />
      ) : (
        // Мельче зелёного: это не тревога, а напоминание.
        <Mail size={12} style={{ color: STATUS_COLORS.danger }} />
      )}
    </span>
  );
}

/**
 * Полоска «сделано / осталось» за период.
 *
 * Сегменты разделены зазором и скруглены по отдельности: сплошная заливка
 * читалась как одна линия, и граница между долями терялась.
 */
function StatusMix({
  total,
  done,
  queued,
  hold,
  /**
   * Подпись под полосой. Выключается там, где окрашенные числа стоят прямо
   * НАД полосой: тогда «Готово · В очереди» повторяет их словами.
   */
  сЛегендой = true,
  className = '',
}: {
  total: number;
  done: number;
  queued: number;
  hold: number;
  сЛегендой?: boolean;
  className?: string;
}) {
  const segments = [
    { key: 'done', label: 'Готово', count: done, color: STATUS_COLORS.done },
    { key: 'queued', label: 'В очереди', count: queued, color: QUEUED_COLOR },
    { key: 'hold', label: 'Ожидание', count: hold, color: STATUS_COLORS.hold },
  ].filter((segment) => segment.count > 0);

  return (
    <div className={className}>
      {/* На телефоне полоса тонкая: она показывает пропорцию, а не величину. */}
      <div className="flex h-1.5 gap-[3px] rounded-full md:h-2.5">
        {total === 0 ? (
          <div className="flex-1 rounded-full" style={{ background: 'var(--otc-line)' }} />
        ) : (
          segments.map((segment) => (
            <div
              key={segment.key}
              className="rounded-full"
              style={{ width: `${(segment.count / total) * 100}%`, background: segment.color }}
              title={`${segment.label}: ${segment.count}`}
            />
          ))
        )}
      </div>

      {/* Подпись — только на широком экране: числа над полосой уже окрашены
          в те же цвета, и на телефоне это строка ради повторения. */}
      <div className={`mt-1.5 hidden flex-wrap items-center gap-x-3 gap-y-1 ${сЛегендой ? 'md:flex' : ''}`}>
        {total === 0 ? (
          <span className="text-[11px] text-ink2">Заказов за период нет</span>
        ) : (
          segments.map((segment) => (
            <span key={segment.key} className="flex items-center gap-1 text-[11px] text-ink2">
              <span
                className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                style={{ background: segment.color }}
              />
              {segment.label}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Полоса оплаты: сколько из суммы заказов уже получено, а сколько должны.
 *
 * Отдельным кусочком, а не вторым вызовом `StatusMix`: там доли считаются
 * по числу заказов, здесь — по деньгам, и объединять их значило бы заводить
 * общий «универсальный» вид, который каждый раз приходилось бы уточнять.
 */
function ПолосаОплаты({
  sumKop,
  debtKop,
  className = '',
}: {
  sumKop: number;
  debtKop: number;
  className?: string;
}) {
  const оплачено = Math.max(0, sumKop - debtKop);
  const доли = [
    { ключ: 'paid', подпись: 'Оплачено', сумма: оплачено, цвет: STATUS_COLORS.done },
    { ключ: 'debt', подпись: 'Задолженность', сумма: debtKop, цвет: STATUS_COLORS.danger },
  ].filter((доля) => доля.сумма > 0);

  return (
    <div className={className}>
      {/* Та же высота и те же просветы, что у полосы работ: две полосы стоят
          в одной строке сводки и обязаны читаться как пара, а не как две
          разные затеи. */}
      <div className="flex h-1.5 gap-[3px] rounded-full md:h-2.5">
        {sumKop <= 0 ? (
          <div className="flex-1 rounded-full" style={{ background: 'var(--otc-line)' }} />
        ) : (
          доли.map((доля) => (
            <div
              key={доля.ключ}
              className="rounded-full"
              style={{ width: `${(доля.сумма / sumKop) * 100}%`, background: доля.цвет }}
              title={`${доля.подпись}: ${formatKop(доля.сумма)} ₽`}
            />
          ))
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      {/*
        Межстрочный интервал прижат: подпись и число читаются как одно.
        На телефоне и то и другое мельче обычного: сводка занимала там треть
        экрана, то есть три заказа, а смотрят на неё мельком.
      */}
      <p className="text-[9px] leading-none text-ink2 md:text-xs md:leading-tight">{label}</p>
      <p
        className="tabular text-[12px] font-semibold leading-tight md:text-lg md:font-medium"
        style={{ color: color ?? 'var(--otc-ink)' }}
      >
        {value}
      </p>
    </div>
  );
}

/** Заголовок колонки. Без `field` — колонка не сортируется. */
function Th({
  label,
  field,
  sort,
  setSort,
  id,
  onResize,
}: {
  label: string;
  field?: SortField;
  sort: { field: SortField; dir: 'asc' | 'desc' };
  setSort: (value: { field: SortField; dir: 'asc' | 'desc' }) => void;
  /** Ключ колонки для запоминания ширины. Без него колонка не тянется. */
  id?: string;
  onResize?: (id: string, event: React.PointerEvent<HTMLElement>) => void;
}) {
  const active = field !== undefined && sort.field === field;

  return (
    /*
      Сортировка висит на ПОДПИСИ, а не на всей ячейке.

      Пока она была на ячейке, перетаскивание ширины заодно меняло порядок
      строк: ручка лежит внутри заголовка, и щелчок с неё всплывал наверх.
      Гасить всплытие оказалось недостаточно — при уводе курсора за пределы
      ручки браузер отдавал щелчок самой ячейке.
    */
    /*
      Шапка прилипает к верху окна.

      Прокручивается окно, а не таблица, поэтому `top: 0` отсчитывается от
      края экрана — как раз то, что нужно. В журнале на полторы сотни строк
      без этого к двадцатой строке уже непонятно, какая колонка где: цифры
      идут столбиками, а подписи уехали.

      Фон обязателен и непрозрачен: под прилипшей шапкой проезжают строки,
      и на просвет она читалась бы как ещё одна строка данных.
    */
    <th
      className="sticky top-0 z-10 whitespace-nowrap px-6 py-2 text-left text-xs font-semibold text-white"
      style={{ background: 'var(--otc-table-head)' }}
    >
      <span
        className={`inline-flex items-center gap-1${field ? ' cursor-pointer' : ''}`}
        /*
         * Три состояния по кругу: возрастание → убывание → СБРОС.
         *
         * Раньше их было два, и отменить сортировку было нечем: нажав на
         * «Оплату», из неё уже не выйти — журнал так и оставался
         * перетасованным, а привычный порядок заведения возвращался только
         * перезагрузкой страницы. Третье нажатие возвращает его же.
         *
         * Сбрасываем в «entered», а не в пустоту: какой-то порядок у списка
         * есть всегда, и порядок заведения — тот, в котором заказы вносили,
         * то есть исходный вид журнала.
         */
        title={
          field
            ? active && sort.dir === 'desc'
              ? 'Нажмите, чтобы вернуть обычный порядок'
              : 'Нажмите, чтобы отсортировать'
            : undefined
        }
        onClick={() => {
          if (!field) return;
          if (!active) return setSort({ field, dir: 'asc' });
          if (sort.dir === 'asc') return setSort({ field, dir: 'desc' });
          setSort({ field: 'entered', dir: 'asc' });
        }}
      >
        {label}
        {/*
          Стрелка сортировки: одиннадцать пикселей, не девять. На девяти
          треугольник в шапке превращался в точку — по нему не понять,
          в какую сторону отсортирован столбец, а именно это он и сообщает.
          Неактивные приглушены прозрачностью, а не размером.
        */}
        {field && (
          <span style={{ opacity: active ? 1 : 0.4, fontSize: 11, lineHeight: 1 }}>
            {active && sort.dir === 'desc' ? '▼' : '▲'}
          </span>
        )}
      </span>

      {/*
        Ручка ширины — на правой границе заголовка, и она ВИДНА.

        Была прозрачной до наведения: о том, что колонку можно тянуть, знал
        только тот, кому об этом сказали. Теперь это светлая черта в самой
        границе — заметная, но не спорящая с подписями.
      */}
      {id && onResize && (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Ширина колонки «${label}»`}
          title="Потяните, чтобы изменить ширину колонки"
          onPointerDown={(event) => {
            event.preventDefault();
            onResize(id, event);
          }}
          className="group absolute inset-y-0 right-0 flex w-3 cursor-col-resize touch-none select-none items-center justify-center"
        >
          <span className="h-4 w-0.5 rounded-full bg-white/40 transition-colors group-hover:h-full group-hover:w-1 group-hover:bg-white/80" />
        </span>
      )}
    </th>
  );
}

/**
 * Ключ «порядка заведения» для разрешения ничьих в сортировке.
 *
 * У сохранённого заказа это момент создания. У НЕСОХРАНЁННОГО его ещё нет —
 * такие заказы уходят в конец списка (они и правда самые новые), а между
 * собой выстраиваются по номеру черновика: временный номер выдаётся
 * счётчиком, и его порядок совпадает с порядком заведения. Номер дополняется
 * нулями, иначе десятый черновик встал бы перед вторым.
 */
function creationOrder(order: ApiOrder): string {
  if (order.createdAt) return `0${order.createdAt}`;
  const counter = Number(order.id.split('_')[1] ?? 0);
  return `1${String(counter).padStart(6, '0')}`;
}

function toDraft(order: ApiOrder): OrderDraft {
  return {
    id: order.id,
    doctorId: order.doctorId,
    clinicId: order.clinicId,
    branchId: order.branchId,
    constructionId: order.constructionId,
    // Заказы, заведённые до появления поля, считаются заказами на один аппарат.
    constructionQty: order.constructionQty ?? 1,
    // Челюсть у прежних заказов не указана — приписки к работе не появится.
    jaw: order.jaw ?? '',
    colors: order.colors ?? [],
    extras: order.extras ?? [],
    patient: order.patient,
    received: order.received,
    due: order.due,
    status: order.status,
    urgency: order.urgency,
    pay: order.pay,
    paid: order.paid,
    costKop: order.costKop,
    expenseKop: order.expenseKop,
    notes: order.notes,
    invoiceId: order.invoiceId,
    invoiceNumber: order.invoiceNumber,
  };
}
